import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { MdKeyboard } from "react-icons/md";
import axios from 'axios';
import useAuth from '../../Users/hooks/useAuth';
import { getLanguageFlag } from "./flags";

import { BsArrowsCollapse, BsArrowsExpand } from "react-icons/bs";




// Import UI Components
import { KeyboardHelpModal } from './components/KeyboardHelpModal';

import { MobileHeader } from './components/MobileHeader';

import ProgressPill from './ProgressPill';

import ProgressModal from "./ProgressModal";

import ExerciseHelpModal from './ExerciseHelpModal';
import ContextHelpModal from "./ContextHelpModal";
import IntonationHelpModal from "./intonation/IntonationHelpModal";
import ChapterCommentsPanel from "./SnapsWise/comments/ChapterCommentsPanel";
import { loadCourse } from './guidelines/loadCourse';



import WordActionMenu from "./SnapsWise/WordActionMenu";
import PresentationModeView from "./SnapsWise/PresentationModeView";
import PracticeModeView from "./SnapsWise/PracticeModeView";
import PresentationModeToggle from "./SnapsWise/PresentationModeToggle";



const VISIBLE_VISUALIZATION_VIEWS = ["pitch", "music", "combined"];

const EMPTY_COMMENT_COUNTS = Object.freeze({
  segment: 0,
  segmentWithDeleted: 0,
  segmentThreads: 0,
  segmentThreadsWithDeleted: 0,
  chapter: 0,
  chapterWithDeleted: 0,
  chapterThreads: 0,
  chapterThreadsWithDeleted: 0,
  all: 0,
  allWithDeleted: 0,
  allThreads: 0,
  allThreadsWithDeleted: 0,
  rateMe: 0,
  rateMeWithDeleted: 0,
});

const COMMENT_COUNT_CACHE_TTL_MS = 30_000;
const commentCountCache = new Map();
const commentCountInflight = new Map();

const normalizeCommentCounts = (json = {}) => ({
  segment: json.segment || 0,
  segmentWithDeleted: json.segmentWithDeleted || 0,
  segmentThreads: json.segmentThreads || 0,
  segmentThreadsWithDeleted: json.segmentThreadsWithDeleted || 0,
  chapter: json.chapter || 0,
  chapterWithDeleted: json.chapterWithDeleted || 0,
  chapterThreads: json.chapterThreads || 0,
  chapterThreadsWithDeleted: json.chapterThreadsWithDeleted || 0,
  all: json.allChapters || 0,
  allWithDeleted: json.allChaptersWithDeleted || 0,
  allThreads: json.allChapterThreads || 0,
  allThreadsWithDeleted: json.allChapterThreadsWithDeleted || 0,
  rateMe: json.rateMeRequests || 0,
  rateMeWithDeleted: json.rateMeRequestsWithDeleted || 0,
});

// Custom hook for mobile detection
const useIsMobile = () => {
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const checkDevice = () => {
            setIsMobile(window.innerWidth < 1280);
        };
        
        checkDevice();
        window.addEventListener('resize', checkDevice);
        
        return () => {
            window.removeEventListener('resize', checkDevice);
        };
    }, []);

    return isMobile;
};

// Shadowing timing configuration



const SHADOWING_TIMING = {
  CUE_TOTAL_DURATION: 2200,
  PULSE_1_TIME: 200,
  PULSE_2_TIME: 800,
  PULSE_3_TIME: 1600,
  CUE_CLEAR_TIME: 600,
  RECORDING_START_OFFSET: -100,
  AUDIO_START_DELAY: 2200,
  POST_AUDIO_BUFFER: 200,
};

// Echoing preparation cue: deliberate enough to perceive each stage.
const ECHOING_TIMING = {
  PULSE_1_TIME: 300,
  PULSE_2_TIME: 900,
  PULSE_3_TIME: 1500,
  CUE_CLEAR_TIME: 500,
  TOTAL_DURATION: 2000,
};

const normalizeShareId = (value) => {
  if (value == null) return null;
  if (typeof value === "object" && value.$oid) return String(value.$oid);
  const normalized = String(value).trim();
  return normalized || null;
};

const getSubjectShareId = (subject) => {
  const explicitSubjectId = normalizeShareId(
    subject?._id?.$oid ||
      subject?._id ||
      subject?.subject_id ||
      subject?.subjectId ||
      subject?.mongo_id
  );

  if (explicitSubjectId) return explicitSubjectId;

  const hasCourseIdentity = Boolean(subject?.course_id || subject?.course_key);
  return hasCourseIdentity ? null : normalizeShareId(subject?.id);
};

const getCourseShareId = (subject) =>
  normalizeShareId(subject?.course_id || subject?.course_key);

const buildRecordingShareContext = ({
  subject,
  file,
  partIndex,
  sentenceIndex,
  practiceMode,
}) => {
  const subjectId = getSubjectShareId(subject);
  const courseId = getCourseShareId(subject);
  const safePartIndex = Math.max(0, Math.trunc(Number(partIndex) || 0));
  const safeSentenceIndex = Math.max(0, Math.trunc(Number(sentenceIndex) || 0));

  if (!subjectId && !courseId) return null;

  return {
    version: 1,
    routeKind: subjectId ? "subject" : "course",
    subjectId,
    courseId,
    partIndex: safePartIndex,
    sentenceIndex: safeSentenceIndex,
    practiceMode: practiceMode || "recording",
    sourceFile: file?.file || null,
  };
};

const SnapsWise = ({
  subject,
  file,
  sectionTitle,
  partIndex = 0,
  initialSentenceIndex = 0,
  onSentenceIndexChange,
  onSharedLocationSaved,
})=>{
   
    const [showIntonationHelp, setShowIntonationHelp] = useState(false);

    // ============================================
    // SECTION 1: STATE MANAGEMENT
    // ============================================
    const [protectedAssessmentAudioUrl, setProtectedAssessmentAudioUrl] = useState(null);
    const [protectedNarratorAudioUrl, setProtectedNarratorAudioUrl] = useState(null);
    const [narratorFragmentAudioUrl, setNarratorFragmentAudioUrl] = useState(null);
    const [narratorFragmentLoading, setNarratorFragmentLoading] = useState(false);
    const [narratorFragmentError, setNarratorFragmentError] = useState(null);
    const [modelSize, setModelSize] = useState("turbo");
    const [rate, setRate] = useState(1);
    const [loading, setLoading] = useState(false);
    const [segment, setSegment] = useState({});
    const [sentenceShareStatus, setSentenceShareStatus] = useState(null);
    const [isgranted, setIsGranted] = useState(false);
    const [stream, setStream] = useState();
    const [audio, setAudio] = useState(null);
    const [hasRecording, setHasRecording] = useState(false);
    const [assessment, setAssessment] = useState(null);
    const { auth, setAuth } = useAuth();
    const token = localStorage.getItem("token");
    const isGuest = auth?.username === "guest";
    const isPublic = !token || isGuest;
    const canUsePronunciationApi = !!token && !isGuest;
  
    const [segments, setSegments] = useState();
    const [broadcastTime, setBroadcastTime] = useState(0);
    
  
    const [focus, setFocus] = useState({});
    const [focused, setEdFocus] = useState();
    
  
    const [translation, setTranslation] = useState();
    
    // State for visualization and layout
    const [showIntonationVisualization, setShowIntonationVisualization] = useState(true);
    const [viewMode, setViewMode] = useState("practice");
    const [visualizationView, setVisualizationView] = useState('pitch');

    useEffect(() => {
      if (!VISIBLE_VISUALIZATION_VIEWS.includes(visualizationView)) {
        setVisualizationView("pitch");
      }
    }, [visualizationView]);
    const [activeTab, setActiveTab] = useState('sentence');
    const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [showKeyboardHelp, setShowKeyboardHelp] = useState(false);
    

    const [showComments, setShowComments] = useState(false);
    const [commentCounts, setCommentCounts] = useState(() => ({
      ...EMPTY_COMMENT_COUNTS,
    }));
    // New state for translation modal
    const [showTranslationModal, setShowTranslationModal] = useState(false);
    
    // Practice mode states
    const [practiceMode, setPracticeMode] = useState('echoing');
    const [preservedMode, setPreservedMode] = useState('echoing');
    const [writeEchoStage, setWriteEchoStage] = useState('write');
    const isEchoPracticeMode =
      practiceMode === 'echoing' || practiceMode === 'writeEcho';

    const isNarratorFragmentReady =
      Boolean(narratorFragmentAudioUrl) &&
      !narratorFragmentLoading &&
      !narratorFragmentError;

    const isNarratorFragmentBlocked =
      !isNarratorFragmentReady;

    const narratorFragmentStatusText = narratorFragmentError
      ? "Audio unavailable"
      : "Preparing audio…";
    
    // Echoing mode states
    const [echoingPhase, setEchoingPhase] = useState('idle');
    const [echoingTimeout, setEchoingTimeout] = useState(null);
    const [echoingPulseCount, setEchoingPulseCount] = useState(0);
    const [echoingTimeouts, setEchoingTimeouts] = useState([]);
    const echoingPhaseRef = useRef('idle');

    //word lane
    const wordPlayRafRef = useRef(null);
    const wordPlayEndRef = useRef(null);
    const wordPlayRequestIdRef = useRef(0);


 const getRecorderMimeType = () => {
  if (typeof MediaRecorder === "undefined") return "";

  const ua = navigator.userAgent || "";
  const isFirefox = ua.includes("Firefox/");
  const isOpera = ua.includes("OPR/") || ua.includes("Opera");

  const candidates = isFirefox
    ? [
        "audio/ogg;codecs=opus",
        "audio/webm;codecs=opus",
        "audio/ogg",
        "audio/webm",
      ]
    : isOpera
    ? [
        "audio/webm;codecs=opus",
        "audio/webm",
        "audio/mp4",
      ]
    : [
        "audio/webm;codecs=opus",
        "audio/webm",
        "audio/mp4",
        "audio/ogg;codecs=opus",
      ];

  for (const type of candidates) {
    try {
      if (MediaRecorder.isTypeSupported?.(type)) return type;
    } catch (e) {}
  }

  return "";
};
const getRecorderOptions = () => {
  const pickedMimeType = getRecorderMimeType();
  return pickedMimeType ? { mimeType: pickedMimeType } : undefined;
};

const createRecorder = (mediaStream) => {
  if (!(mediaStream instanceof MediaStream)) {
    throw new Error("createRecorder expected a MediaStream");
  }

  const options = getRecorderOptions();
  return options
    ? new MediaRecorder(mediaStream, options)
    : new MediaRecorder(mediaStream);
};


    const stopWordPlayRAF = () => {
  if (wordPlayRafRef.current) {
    cancelAnimationFrame(wordPlayRafRef.current);
    wordPlayRafRef.current = null;
  }
  wordPlayEndRef.current = null;
};

const playRangePrecise = (absoluteStart, absoluteEnd) => {
  const audioElement = audioref.current;
  const segmentStart = Number(segment?.start);
  const segmentEnd = Number(segment?.end);

  if (
    !audioElement ||
    !isNarratorFragmentReady ||
    !Number.isFinite(segmentStart) ||
    !Number.isFinite(segmentEnd)
  ) {
    return;
  }

  stopWordPlayRAF();
  wordPlayRequestIdRef.current += 1;
  const requestId = wordPlayRequestIdRef.current;

  const segmentDuration = Math.max(0, segmentEnd - segmentStart);
  const start = Math.max(
    0,
    Math.min(segmentDuration, Number(absoluteStart) - segmentStart)
  );
  const end = Math.max(
    start,
    Math.min(segmentDuration, Number(absoluteEnd) - segmentStart)
  );

  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) {
    return;
  }

  wordPlayEndRef.current = end;
  audioElement.currentTime = start;

  const playPromise = audioElement.play();
  if (playPromise && typeof playPromise.catch === "function") {
    playPromise.catch((error) => {
      console.error("Could not play the selected fragment range:", error);
      stopWordPlayRAF();
      setIsPlayingSegment(false);
    });
  }

  const tick = () => {
    const currentAudio = audioref.current;
    const targetEnd = wordPlayEndRef.current;

    if (
      !currentAudio ||
      targetEnd == null ||
      requestId !== wordPlayRequestIdRef.current
    ) {
      return;
    }

    if (currentAudio.currentTime >= targetEnd) {
      currentAudio.pause();
      stopWordPlayRAF();
      setIsPlayingSegment(false);
      return;
    }

    wordPlayRafRef.current = requestAnimationFrame(tick);
  };

  wordPlayRafRef.current = requestAnimationFrame(tick);
};

const handleLaneWordClick = (wordRelative) => {
  if (
    isNarratorFragmentBlocked ||
    !wordRelative ||
    segment?.start == null
  ) {
    return;
  }

  cancelAllProcesses();
  setFocus(segment);
  setIsPlayingSegment(true);

  const segmentStart = Number(segment.start);
  const relativeStart = Number(wordRelative.start);
  const relativeEnd = Number(wordRelative.end);

  playRangePrecise(
    segmentStart + relativeStart,
    segmentStart + relativeEnd
  );
};
    
    // Shadowing mode states
    const [isShadowing, setIsShadowing] = useState(false);
    const [shadowingTimeout, setShadowingTimeout] = useState(null);
    const [showFirstWordCue, setShowFirstWordCue] = useState(false);
    const [cuePulseCount, setCuePulseCount] = useState(0);
    const [isCueActive, setIsCueActive] = useState(false);
    const [cueTimeouts, setCueTimeouts] = useState([]);
    
    // Recording states
    const [recordingStatus, setRecordingStatus] = useState("inactive");
    const [audioChunks, setAudioChunks] = useState([]);
    const [duration, setDuration] = useState(6);
    const [isPlayingSegment, setIsPlayingSegment] = useState(false);
    const [currentWord, setCurrentWord] = useState(0);
    const [recordingProgress, setRecordingProgress] = useState(0);
    const [maxRecordingTime, setMaxRecordingTime] = useState(0);

   
    const STEP_SECONDS = 1 * 60;
    const MIN_GOAL_SECONDS = 1 * 60; // change later if you want
    const [todaySeconds, setTodaySeconds] = useState(0);
    const [showProgressModal, setShowProgressModal] = useState(false);
    const [goalSeconds, setGoalSeconds] = useState(5 * 60);

    const [wordMenu, setWordMenu] = useState(null);

    const [course, setCourse] = useState(null);
    const [showExerciseHelp, setShowExerciseHelp] = useState(false);

    // --- Help intro (expanded anchor on exercise load) ---
    const [showHelpIntro, setShowHelpIntro] = useState(false);
    const helpIntroTimerRef = useRef(null);
    const lastIntroKeyRef = useRef(null);

    const deriveExerciseId = (subject, file) => {
  const key = subject?.course_key;
  const name = file?.file;
  if (!key || !name) return null;

  // ✅ Flyt og rytme naming scheme ONLY
  if (key === "flyt-og-rytme") {
    // Matches: 01a_nodoubles.mp3, 01A.mp3, 2b-something.mp3
    const m = String(name).match(/^(\d{1,2})\s*([abAB])(?=[._-]|$)/);
    if (!m) return null;
    return `${parseInt(m[1], 10)}${m[2].toUpperCase()}`;
  }

  // ✅ Other courses later:
  // if (key === "other-course") { ... }

  return null;
};


    const openWordMenu = (e, text, i) => {
  e.preventDefault();
  e.stopPropagation();
  const r = e.currentTarget.getBoundingClientRect();
  setWordMenu({ text, i, rect: { left: r.left, top: r.top, width: r.width, height: r.height } });
};

const closeWordMenu = () => setWordMenu(null);

const handleWriteEchoStageChange = (stage) => {
  setWriteEchoStage(stage);
  const targetIsVisible = stage === 'review' || stage === 'result';

  if (!targetIsVisible) {
    closeWordMenu();
    setTranslation(null);
    setShowTranslationModal(false);
    setShowComments(false);
  }
};

const buildCommentCountRequest = () => {
  if (!subject || !file?.file || !segment?.text) return null;

  const body = {
    subject: {
      author: subject.author,
      title: subject.title,
      language: subject.language,
      type: subject.type,
      owner: subject.owner || null,
    },
    file: file.file,
    anchor: {
      start: segment.start,
      end: segment.end,
      text: segment.text,
    },
  };

  const key = JSON.stringify([
    body.subject.owner,
    body.subject.language,
    body.subject.author,
    body.subject.title,
    body.subject.type,
    body.file,
    body.anchor.start,
    body.anchor.end,
    body.anchor.text,
  ]);

  return { key, body };
};

const fetchCommentCounts = async () => {
  const request = buildCommentCountRequest();

  if (!request) {
    commentCountAnchorKeyRef.current = "";
    setCommentCounts({ ...EMPTY_COMMENT_COUNTS });
    return;
  }

  const { key, body } = request;
  commentCountAnchorKeyRef.current = key;

  const cached = commentCountCache.get(key);
  if (cached && Date.now() - cached.savedAt < COMMENT_COUNT_CACHE_TTL_MS) {
    setCommentCounts(cached.counts);
    return;
  }

  try {
    let pending = commentCountInflight.get(key);

    if (!pending) {
      pending = fetch(
        `${process.env.REACT_APP_USER_API_URL}/comments/counts`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...authHeaders(),
          },
          body: JSON.stringify(body),
        }
      )
        .then(async (res) => {
          if (!res.ok) {
            throw new Error(`Comment counts failed (${res.status}).`);
          }

          return normalizeCommentCounts(await res.json());
        })
        .then((counts) => {
          commentCountCache.set(key, { counts, savedAt: Date.now() });
          return counts;
        })
        .finally(() => {
          commentCountInflight.delete(key);
        });

      commentCountInflight.set(key, pending);
    }

    const counts = await pending;

    // A slower response for the previous sentence must never overwrite the
    // counts for the sentence that is active now.
    if (commentCountAnchorKeyRef.current === key) {
      setCommentCounts(counts);
    }
  } catch (e) {
    console.error("fetchCommentCounts failed:", e);
  }
};

const fetchProtectedAudioBlobUrl = async (audioUrl) => {
  const token = localStorage.getItem("token");
  if (!audioUrl || !token) return null;

  try {
    const response = await axios.get(
      `${process.env.REACT_APP_USER_API_URL}${audioUrl}`,
      {
        responseType: "blob",
        headers: { Authorization: `Bearer ${token}` },
      }
    );

    return URL.createObjectURL(response.data);
  } catch (error) {
    console.error("Failed to fetch protected audio:", error);
    return null;
  }
};

const resolveBackendAudioUrl = (value) => {
  if (!value) return null;

  // already absolute
  if (typeof value === "string" && value.startsWith("http")) {
    return value;
  }

  // backend relative route like /audio/... or /upload/audio/... or /uploads/...
  if (typeof value === "string" && value.startsWith("/")) {
    return `${process.env.REACT_APP_USER_API_URL}${value}`;
  }

  // old-style relative path fallback
  if (typeof value === "string") {
    return `${process.env.REACT_APP_USER_API_URL}/${value}`;
  }

  return null;
};

    const assessmentAudioUrl = resolveBackendAudioUrl(
  assessment?.audio_url ||
  assessment?.intonation?.audio_url ||
  assessment?.file
);

const narratorAssessmentAudioUrl = resolveBackendAudioUrl(
  assessment?.narrator_intonation?.audio_url ||
  assessment?.narrator_intonation?.file_narrator
);

const isPrivateAudioRoute = (url) =>
  typeof url === "string" && url.includes("/audio/private/");

const resolvedAssessmentAudioUrl = isPrivateAudioRoute(assessmentAudioUrl)
  ? protectedAssessmentAudioUrl || null
  : assessmentAudioUrl || null;

const resolvedNarratorAssessmentAudioUrl = isPrivateAudioRoute(narratorAssessmentAudioUrl)
  ? protectedNarratorAudioUrl || null
  : narratorAssessmentAudioUrl || null;

const authHeaders = () => {
  const t = localStorage.getItem("token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};
const handleAuthExpired = () => {
  // Soft mode: just drop token + fall back to public mode
  localStorage.removeItem("token");

  // Optional: if you want UI to clearly switch to guest styling
  try {
    setAuth((prev) => ({ ...(prev || {}), username: "guest", role: prev?.role }));
  } catch (e) {
    // ignore if setAuth not available in some contexts
  }
};


useEffect(() => {
  if (
    !subject?.owner ||
    !subject?.language ||
    !subject?.author ||
    !subject?.title ||
    !subject?.type ||
    !file?.file ||
    segment?.start == null ||
    segment?.end == null
  ) {
    setNarratorFragmentAudioUrl(null);
    setNarratorFragmentError(null);
    setNarratorFragmentLoading(false);
    return;
  }

  const controller = new AbortController();
  let active = true;

  const encodePathPart = (value) =>
    encodeURIComponent(String(value ?? ""));

  const loadNarratorFragment = async () => {
    setNarratorFragmentLoading(true);
    setNarratorFragmentError(null);
    setNarratorFragmentAudioUrl(null);

    const url =
      `${process.env.REACT_APP_USER_API_URL}/analysis/narrator/` +
      `${encodePathPart(subject.owner)}/` +
      `${encodePathPart(subject.language)}/` +
      `${encodePathPart(subject.author)}/` +
      `${encodePathPart(subject.title)}/` +
      `${encodePathPart(file.file)}/` +
      `${encodePathPart(segment.start)}/` +
      `${encodePathPart(segment.end)}/` +
      `${encodePathPart(subject.type)}`;

    try {
      const formData = new FormData();
      formData.append("text", segment?.text || "");

      const response = await axios.post(url, formData, {
        signal: controller.signal,
        headers: authHeaders(),
      });

      const narrator = response?.data?.narrator_intonation;
      const fragmentPath =
        narrator?.fragment?.url ||
        narrator?.audio_url ||
        response?.data?.segment_audio_url ||
        narrator?.file_narrator;

      const resolvedUrl = resolveBackendAudioUrl(fragmentPath);
      if (!resolvedUrl) {
        throw new Error("Narrator analysis did not return a fragment audio URL.");
      }

      if (active) {
        setNarratorFragmentAudioUrl(resolvedUrl);
      }
    } catch (error) {
      if (
        error?.code === "ERR_CANCELED" ||
        error?.name === "CanceledError"
      ) {
        return;
      }

      console.error("Failed to load narrator fragment:", error);
      if (active) {
        setNarratorFragmentAudioUrl(null);
        setNarratorFragmentError(
          error?.response?.data?.detail ||
          error?.message ||
          "Failed to load narrator fragment."
        );
      }
    } finally {
      if (active) {
        setNarratorFragmentLoading(false);
      }
    }
  };

  loadNarratorFragment();

  return () => {
    active = false;
    controller.abort();
  };
}, [
  subject?.owner,
  subject?.language,
  subject?.author,
  subject?.title,
  subject?.type,
  file?.file,
  segment?.start,
  segment?.end,
  segment?.text,
]);

const renderSentenceSharePill = () => {
  if (!subjectShareId || !Array.isArray(segments) || segments.length === 0) {
    return null;
  }

  return (
    <div className="relative shrink-0">
      <button
        type="button"
        onClick={copySentenceShareLink}
        title="Copy a link to this sentence"
        aria-label="Copy a link to this sentence"
        className={[
          "group inline-flex h-9 w-9 shrink-0 items-center justify-center gap-2 rounded-full p-0 lg:w-auto lg:px-3",
          "border border-slate-200/90 bg-white text-sm font-semibold text-slate-700",
          "shadow-sm transition-[transform,background-color,border-color,box-shadow,color] duration-200",
          "hover:-translate-y-px hover:border-blue-200 hover:bg-blue-50/65 hover:text-blue-700 hover:shadow",
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400/65 focus-visible:ring-offset-2",
        ].join(" ")}
      >
        <svg
          className="h-4 w-4 shrink-0"
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M15 8a3 3 0 10-2.83-4H12a3 3 0 00.17 1l-5.2 2.6a3 3 0 00-1.97-.6 3 3 0 100 6c.73 0 1.4-.26 1.97-.7l5.2 2.6a3 3 0 10.63-1.2l-5.32-2.66a3.04 3.04 0 000-1.08l5.32-2.66A2.99 2.99 0 0015 8z" />
        </svg>

        <span className="hidden whitespace-nowrap lg:inline">
          Share sentence
        </span>
      </button>

      {sentenceShareStatus && (
        <div
          className={[
            "absolute right-0 top-full z-[75] mt-1 whitespace-nowrap",
            "rounded-lg border border-slate-200 bg-white px-2 py-1",
            "text-xs font-semibold text-slate-700 shadow-lg",
          ].join(" ")}
          role="status"
        >
          {sentenceShareStatus === "copied" ? "Copied" : "No link"}
        </div>
      )}
    </div>
  );
};

const renderHeaderIdentity = () => {
  const identityIcon = helpMode === "context" ? "🗺️" : "🎯";

  return (
    <div
      className="inline-flex h-10 max-w-full min-w-0 cursor-default select-none items-center gap-2.5"
      aria-label={headerTitle}
    >
      <span
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-[13px] ring-1 ring-blue-100/90"
        aria-hidden="true"
      >
        {identityIcon}
      </span>

      <span className="truncate text-[15px] font-semibold tracking-[-0.015em] text-slate-900 sm:text-base">
        {headerTitle}
      </span>
    </div>
  );
};

const renderGuidePill = () => {
  const hasExerciseGuide = helpEnabled && hasExerciseHelp;
  const hasContextGuide = helpEnabled && hasContextHelp;

  if (!hasExerciseGuide && !hasContextGuide) return null;

  const guideLabel = hasExerciseGuide
    ? exerciseId
      ? `Exercise ${exerciseId} guide`
      : "Exercise guide"
    : helpActionLabel;

  const introIsAvailable =
    hasExerciseGuide && showHelpIntro && Boolean(introAnchorTitle);

  return (
    <button
      type="button"
      onClick={() => setShowExerciseHelp(true)}
      className={[
        "group inline-flex h-9 w-9 shrink-0 items-center justify-center gap-2 rounded-full p-0 lg:w-auto lg:px-3.5",
        "border border-blue-200 bg-blue-50/85 text-sm font-bold text-blue-700",
        "shadow-sm transition-[transform,background-color,border-color,box-shadow,color] duration-200",
        "hover:-translate-y-px hover:border-blue-300 hover:bg-blue-100/75 hover:shadow",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400/70 focus-visible:ring-offset-2",
        introIsAvailable ? "ring-2 ring-blue-200/80 ring-offset-1" : "",
      ].join(" ")}
      title={
        introAnchorTitle && hasExerciseGuide
          ? `${guideLabel}: ${introAnchorTitle}`
          : `Open ${guideLabel}`
      }
      aria-label={
        hasExerciseGuide
          ? `Open the guide for Exercise ${exerciseId || "this exercise"}`
          : `Open ${guideLabel}`
      }
    >
      <span
        className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-blue-200 bg-white text-[11px] font-black text-blue-700 transition-colors group-hover:border-blue-300"
        aria-hidden="true"
      >
        i
      </span>

      <span className="hidden whitespace-nowrap lg:inline">
        {guideLabel}
      </span>

      <span className="hidden text-sm leading-none text-blue-500 transition-transform duration-200 group-hover:translate-x-0.5 lg:inline" aria-hidden="true">
        ›
      </span>
    </button>
  );
};

const renderCommentsPill = () => {
  if (!subject || !file || !segment?.text) return null;

  const total = commentCounts.segment + commentCounts.chapter;

  return (
    <button
      type="button"
      onClick={() => setShowComments(true)}
      className="group relative inline-flex h-9 w-9 shrink-0 items-center justify-center gap-2 rounded-full border border-slate-200/90 bg-white p-0 text-sm font-semibold text-slate-700 shadow-sm transition-[transform,background-color,border-color,box-shadow,color] duration-200 hover:-translate-y-px hover:border-blue-200 hover:bg-blue-50/65 hover:text-blue-700 hover:shadow lg:w-auto lg:px-3.5"
      title="Open comments"
      aria-label={`Open comments. ${commentCounts.segment} sentence and ${commentCounts.chapter} chapter comments.`}
    >
      <span className="flex h-6 w-6 items-center justify-center rounded-full border border-blue-200 bg-white text-xs">
        💬
      </span>

      <span className="hidden lg:inline">Comments</span>

      <span className="hidden text-xs text-gray-500 lg:inline">
        {commentCounts.segment}/{commentCounts.chapter}
      </span>

      {total > 0 && (
        <span className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-blue-500 ring-2 ring-white lg:static lg:ring-0" />
      )}
    </button>
  );
};
const fetchDailyGoal = async (language) => {
  if (!language) return;
  try {
    const res = await fetch(
      `${process.env.REACT_APP_USER_API_URL}/goals/daily?language=${encodeURIComponent(language)}`,
      { headers: authHeaders() }
    );

    if (res.status === 401 || res.status === 403) return handleAuthExpired();
    if (!res.ok) return;

    const json = await res.json();
    const n = Number(json.goal_seconds);
    setGoalSeconds(Number.isFinite(n) ? n : MIN_GOAL_SECONDS);
  } catch (e) {
    console.error("fetchDailyGoal error:", e);
  }
};


const clampGoal = (s) => Math.min(6 * 60 * 60, Math.max(MIN_GOAL_SECONDS, s));

const updateDailyGoal = async (nextGoalSeconds) => {
  const language = subject?.language;
  if (!language) return;

  const next = clampGoal(nextGoalSeconds);
  

  try {
    const url =
      `${process.env.REACT_APP_USER_API_URL}/goals/daily` +
      `?language=${encodeURIComponent(language)}` +
      `&goal_seconds=${next}`;
  
    const res = await fetch(url, {
      method: "PUT",
      headers: authHeaders(),
    });
    
    if (!res.ok) {
      console.warn("updateDailyGoal failed:", res.status);
      return;
    }

    const json = await res.json();
   

    const n = Number(json.goal_seconds);
    setGoalSeconds(Number.isFinite(n) ? n : next);
  } catch (e) {
    console.error("updateDailyGoal error:", e);
  }
};



 const fetchTodayActivity = async (language) => {
  if (!language) return;
  if (isGuest) return; // <-- guests: do nothing

  try {
    const res = await fetch(
      `${process.env.REACT_APP_USER_API_URL}/activity/today?language=${encodeURIComponent(language)}`,
      { headers: authHeaders() }  // <-- reuse your helper
    );

    if (res.status === 401 || res.status === 403) return handleAuthExpired();
    if (!res.ok) return;

    const json = await res.json();
    setTodaySeconds(json.done_seconds ?? 0);
  } catch (e) {
    console.error("fetchTodayActivity error:", e);
  }
};


    


    // ============================================
    // SECTION 2: REFS
    // ============================================
    const audioRecordRef = useRef(null);
    const mediaRecorderRef = useRef(null);
    const keyboardListenerRef = useRef(null);
    const mediaRecorder = useRef(null);
    const audioref = useRef(null);
    const recordRef = useRef(null);
    const pulseRef = useRef(null);
    const currentHandlerRef = useRef(null);
    const audioChunksRef = useRef([]);
    const activeStreamRef = useRef(null);
    const commentCountAnchorKeyRef = useRef("");
    const onSentenceIndexChangeRef = useRef(onSentenceIndexChange);

    useEffect(() => {
      onSentenceIndexChangeRef.current = onSentenceIndexChange;
    }, [onSentenceIndexChange]);


const setupRecorderHandlers = (recorder) => {
  audioChunksRef.current = [];

  recorder.ondataavailable = (event) => {
    if (event.data && event.data.size > 0) {
      audioChunksRef.current.push(event.data);
    }
  };

  recorder.onerror = (event) => {
    console.error("Recorder error:", event);
  };
};

const buildRecordingBlob = (recorder) => {
  const chunks = audioChunksRef.current;
  if (!chunks.length) return null;

  const mimeType = recorder?.mimeType || getRecorderMimeType() || "audio/webm";
  const blob = new Blob(chunks, { type: mimeType });

  return blob.size > 0 ? blob : null;
};
    

    const isMobile = useIsMobile();

   const audioRecord = useRef(null);

// SnapsWise exercises use only the generated fragment WAV. The complete
// source recording remains an editor asset and is not sought during playback.
const fullaudio = narratorFragmentAudioUrl;

// Keep the fragment synchronized with the shared speed state. Browser
// currentTime already advances on the media timeline at the chosen rate.
useEffect(() => {
  const audioElement = audioref.current;
  if (!audioElement) return;

  audioElement.defaultPlaybackRate = rate;
  audioElement.playbackRate = rate;
  audioElement.preservesPitch = true;
  audioElement.webkitPreservesPitch = true;
}, [rate, fullaudio]);

// Reset the single authoritative exercise audio element whenever a different
// fragment is loaded.
useEffect(() => {
  const audioElement = audioref.current;
  if (!audioElement) return;

  stopWordPlayRAF();

  if (currentHandlerRef.current) {
    audioElement.removeEventListener("timeupdate", currentHandlerRef.current);
    currentHandlerRef.current = null;
  }

  audioElement.pause();

  try {
    audioElement.currentTime = 0;
  } catch (error) {
    // Metadata may not be available yet; loadedmetadata will start at zero.
  }

  audioElement.defaultPlaybackRate = rate;
  audioElement.playbackRate = rate;
  audioElement.preservesPitch = true;
  audioElement.webkitPreservesPitch = true;
  audioElement.load();

  setBroadcastTime(0);
  setCurrentWord(0);
  setIsPlayingSegment(false);
}, [narratorFragmentAudioUrl]);
    // ============================================
    // SECTION 3: UTILITY FUNCTIONS
    // ============================================
    const pulse_frames = [ 
        { transform: "scale(1)", opacity: "1" },
        { transform: "scale(1.1)", opacity: "0.8" },
        { transform: "scale(1)", opacity: "1" },
    ];

    const progress_frames = [ 
        { transform: "translateX(0)" },
        { transform: "translateX(176px)" },
        { transform: "translateX(0)" },
    ];

    // ============================================
    // SECTION 4: CORE FUNCTIONS
    // ============================================
    
    const timeUpdate = (event) => {
  // The exercise audio is a zero-based fragment WAV.
  const currentTime = event.target.currentTime;
  const segmentStart = Number(segment?.start);
  const segmentEnd = Number(segment?.end);
  const segmentDuration =
    Number.isFinite(segmentStart) && Number.isFinite(segmentEnd)
      ? Math.max(0, segmentEnd - segmentStart)
      : 0;

  setBroadcastTime(Math.max(0, Math.min(segmentDuration, currentTime)));

  if (segment?.hasOwnProperty("words") && Number.isFinite(segmentStart)) {
    const foundword = segment.words.findIndex((word, i) => {
      const relativeStart = Number(word.start) - segmentStart;
      const relativeEnd = Number(word.end) - segmentStart;
      const isLast = i === segment.words.length - 1;

      return isLast
        ? relativeStart <= currentTime && relativeEnd >= currentTime
        : relativeStart <= currentTime && relativeEnd > currentTime;
    });

    if (foundword !== -1 && currentWord !== foundword) {
      setCurrentWord(foundword);
    }
  }
};
   
 const sendBlob = ({ blob, subject, file, segment }) => {
  const token = localStorage.getItem("token");

  const url = `${process.env.REACT_APP_USER_API_URL}/analysis/upload_blob/${subject.owner}/${subject.language}/${subject.author}/${subject.title}/${file.file}/${subject.type}`;

  const blobType = (blob?.type || "").toLowerCase();
  const ext =
    blobType.includes("wav") ? "wav" :
    blobType.includes("ogg") ? "ogg" :
    blobType.includes("mp4") || blobType.includes("mpeg") ? "mp4" :
    "webm";

  const filename = `recording_${Date.now()}.${ext}`;

  const formData = new FormData();
  formData.append("uploaded_file", blob, filename);
  formData.append("sendText", JSON.stringify(segment));
  formData.append("tz", Intl.DateTimeFormat().resolvedOptions().timeZone);

  const shareContext = buildRecordingShareContext({
    subject,
    file,
    partIndex,
    sentenceIndex: currentSentenceIndex,
    practiceMode,
  });

  if (shareContext) {
    formData.append("shareContext", JSON.stringify(shareContext));
  }

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  setLoading(true);
  setAssessment(null);

  axios
    .post(url, formData, { headers })
    .then((response) => {
      const data = response.data;

      setAssessment(data);
      setLoading(false);

      if (data?.shared_location?.path) {
        onSharedLocationSaved?.(data.shared_location);
      }

      if (!isPublic) {
        const act = data?.activity;
        if (act && typeof act.daily_total_seconds === "number") {
          setTodaySeconds(act.daily_total_seconds);
        } else {
          fetchTodayActivity(subject.language);
        }
      }
    })
    .catch((error) => {
      const status = error?.response?.status;
      console.error("Error uploading file:", status, error);
      setLoading(false);

      if (token && (status === 401 || status === 403)) {
        handleAuthExpired();
        setAssessment({ error: "AUTH_EXPIRED" });
        return;
      }

      setAssessment({ error: "UPLOAD_FAILED" });
    });
};


const getTranslations = async (info) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_USER_API_URL}/dictionary/get-translation`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders(),
        },
        body: JSON.stringify(info),
      }
    );

    if (response.status === 401 || response.status === 403) {
      handleAuthExpired(); // soften: remain on page
      return;
    }

    if (!response.ok) {
      console.warn("getTranslations failed:", response.status);
      return;
    }

    const json = await response.json();
    setTranslation(json);
  } catch (e) {
    console.error("getTranslations error:", e);
  }
};

/**
 * ✅ PUBLIC-OK (if backend allows it):
 * - sends auth header only if token exists
 * - does not redirect on 401/403
 * - keeps your existing model_size behavior
 */
const getSegments = async ({ subject, file }) => {
  const outdata = {
    author: subject.author,
    title: subject.title,
    language: subject.language,
    file,
    type: subject.type,
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_USER_API_URL}/whisper/get-segments`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders(),
        },
        body: JSON.stringify(outdata),
      }
    );

    if (response.status === 401 || response.status === 403) {
      handleAuthExpired(); // soften: remain on page
      return;
    }

    if (!response.ok) {
      console.warn("getSegments failed:", response.status);
      return;
    }

    const json = await response.json();
    setSegments(json.segments);
    if (json.model_size) setModelSize(json.model_size);
  } catch (e) {
    console.error("getSegments error:", e);
  }
};

   const getMicrophonePermission = async () => {
  if (!("MediaRecorder" in window)) {
    alert("The MediaRecorder API is not supported in your browser.");
    return null;
  }

  try {
    const streamData = await navigator.mediaDevices.getUserMedia({
      audio: true,
      video: false,
    });

    setIsGranted(true);
    setStream(streamData);

    return streamData;
  } catch (err) {
    console.error("Error getting microphone permission:", err);
    alert(err.message);
    return null;
  }
};

    // ============================================
    // SECTION 5: AUDIO PLAYBACK FUNCTIONS
    // ============================================
    
    const Play_record = () => {
  setTimeout(() => {
    if (!audioRecord.current) return;
    audioRecord.current.currentTime = 0;
    audioRecord.current.play();
  }, 300);
};
    const newreachedEnd = (segment) => {
        const handler = (event) => {
            const currentTime = event.target.currentTime;
            const segmentDuration = Math.max(
              0,
              Number(segment?.end ?? 0) - Number(segment?.start ?? 0)
            );

            if (currentTime >= segmentDuration - 0.001) {
                audioref.current.pause();
                audioref.current.currentTime = 0;
                audioref.current.removeEventListener('timeupdate', handler);
                currentHandlerRef.current = null;
                setIsPlayingSegment(false);
                setCurrentWord(0);
                
                if (isShadowing && recordingStatus === "recording") {
                    setTimeout(() => {
                        if (shadowingTimeout) {
                            clearTimeout(shadowingTimeout);
                            setShadowingTimeout(null);
                        }
                        stopShadowingRecording(true);
                    }, 500);
                }
                
                // Check echoing phase from ref (synchronous)
                if (isEchoPracticeMode && echoingPhaseRef.current === 'playing') {
                    startEchoingCountdown();
                }
            }
        };
        return handler;
    };

    

    const handler = newreachedEnd(segment);

    // ============================================
    // SECTION 6: RECORDING FUNCTIONS
    // ============================================
    
 const startRecording = async (sendForAnalysis = true) => {
  let activeStream = stream;

  if (!(activeStream instanceof MediaStream)) {
    try {
      activeStream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: false,
      });
      setStream(activeStream);
      setIsGranted(true);
    } catch (err) {
      console.error("Could not get microphone stream:", err);
      return;
    }
  }

  if (!(activeStream instanceof MediaStream)) {
    console.error("startRecording: invalid MediaStream", activeStream);
    return;
  }

  setRecordingStatus("recording");
  setRecordingProgress(0);

  const maxTime = 3 * duration * 1000;
  setMaxRecordingTime(maxTime);

  const media = createRecorder(activeStream);

  mediaRecorder.current = media;
  mediaRecorderRef.current = media;

  setupRecorderHandlers(media);

  media.start();

  const startTime = Date.now();

  const updateProgress = () => {
    if (mediaRecorder.current?.state === "recording") {
      const elapsed = Date.now() - startTime;
      const progress = Math.min((elapsed / maxTime) * 100, 100);
      setRecordingProgress(progress);

      if (progress < 100) {
        requestAnimationFrame(updateProgress);
      }
    }
  };

  requestAnimationFrame(updateProgress);
};

const stopRecording = (sendForAnalysis = true) => {
  if (!mediaRecorder.current || mediaRecorder.current.state === "inactive") {
    return;
  }

  const recorder = mediaRecorder.current;

  if (sendForAnalysis) {
    setAssessment(null);
    setLoading(true);
  }

  setRecordingProgress(sendForAnalysis ? 100 : 0);

  if (recordRef.current) {
    recordRef.current.getAnimations().forEach((animation) => {
      animation.pause();
    });
  }

  setRecordingStatus("inactive");
  setHasRecording(sendForAnalysis);

  recorder.onstop = () => {
    if (sendForAnalysis) {
      const audioBlob = buildRecordingBlob(recorder);

      if (!audioBlob) {
        console.error("No recording blob created");
        setLoading(false);
        audioChunksRef.current = [];
        return;
      }

      const audioUrl = URL.createObjectURL(audioBlob);

      sendBlob({
        blob: audioBlob,
        subject,
        file,
        segment,
      });

      setAudio(audioUrl);
    }

    audioChunksRef.current = [];
  };

  recorder.stop();
};
    // ============================================
    // SECTION 6.5: PROCESS CANCELLATION FUNCTIONS
    // ============================================
    
    const silentCancelRecording = () => {
  setRecordingStatus("inactive");
  setRecordingProgress(0);
  setIsShadowing(false);
  setIsCueActive(false);
  setShowFirstWordCue(false);
  setCuePulseCount(0);
  setEchoingPhase("idle");
  echoingPhaseRef.current = "idle";
  setEchoingPulseCount(0);

  if (shadowingTimeout) clearTimeout(shadowingTimeout);
  if (echoingTimeout) clearTimeout(echoingTimeout);
  setShadowingTimeout(null);
  setEchoingTimeout(null);

  if (echoingTimeouts.length > 0) {
    echoingTimeouts.forEach((timeout) => timeout && clearTimeout(timeout));
    setEchoingTimeouts([]);
  }

  if (mediaRecorder.current && mediaRecorder.current.state !== "inactive") {
    try {
      mediaRecorder.current.onstop = null;
      mediaRecorder.current.stop();
    } catch (error) {
      console.error("Error stopping recorder:", error);
    }
  }

  setAudioChunks([]);
  audioChunksRef.current = [];

  [recordRef.current, pulseRef.current].forEach((ref) => {
    if (ref) {
      const animations = ref.getAnimations();
      if (animations) {
        animations.forEach((animation) => animation.cancel());
      }
    }
  });
};

    const cancelAllProcesses = () => {

        stopWordPlayRAF();
        if (recordingStatus === 'recording') {
            silentCancelRecording();
        }
        
        if (audioref.current) {
            audioref.current.pause();
            setIsPlayingSegment(false);
            
            if (currentHandlerRef.current) {
                audioref.current.removeEventListener('timeupdate', currentHandlerRef.current);
                currentHandlerRef.current = null;
            }
        }
        
        if (shadowingTimeout) {
            clearTimeout(shadowingTimeout);
            setShadowingTimeout(null);
        }
        
        if (echoingTimeout) {
            clearTimeout(echoingTimeout);
            setEchoingTimeout(null);
        }
        
        if (echoingTimeouts.length > 0) {
            echoingTimeouts.forEach(timeout => timeout && clearTimeout(timeout));
            setEchoingTimeouts([]);
        }
        
        if (cueTimeouts.length > 0) {
            cueTimeouts.forEach(timeout => timeout && clearTimeout(timeout));
            setCueTimeouts([]);
        }
        
        setEchoingPhase('idle');
        echoingPhaseRef.current = 'idle';
        setEchoingPulseCount(0);
        setIsShadowing(false);
        setShowFirstWordCue(false);
        setIsCueActive(false);
    };

    // ============================================
    // SECTION 6.6: SHADOWING FUNCTIONS
    // ============================================
    
    const triggerFirstWordCue = () => {
        if (!segment || !segment.words || segment.words.length === 0) {
            console.error("Cannot trigger cue: segment or words missing");
            return;
        }
        
        setIsCueActive(true);
        setCuePulseCount(0);
        setShowFirstWordCue(true);
        
        if (cueTimeouts.length > 0) {
            cueTimeouts.forEach(timeout => timeout && clearTimeout(timeout));
            setCueTimeouts([]);
        }
        
        const newTimeouts = [];
        
        const timeout1 = setTimeout(() => {
            setCuePulseCount(1);
        }, SHADOWING_TIMING.PULSE_1_TIME);
        newTimeouts.push(timeout1);
        
        const timeout2 = setTimeout(() => {
            setCuePulseCount(2);
        }, SHADOWING_TIMING.PULSE_2_TIME);
        newTimeouts.push(timeout2);
        
        const timeout3 = setTimeout(() => {
            setCuePulseCount(3);
            
            const clearTimeoutId = setTimeout(() => {
                setIsCueActive(false);
                setShowFirstWordCue(false);
                setCuePulseCount(0);
            }, SHADOWING_TIMING.CUE_CLEAR_TIME);
            newTimeouts.push(clearTimeoutId);
        }, SHADOWING_TIMING.PULSE_3_TIME);
        newTimeouts.push(timeout3);
        
        setCueTimeouts(prev => [...prev, ...newTimeouts]);
        
        const recordingStartTime = SHADOWING_TIMING.AUDIO_START_DELAY + SHADOWING_TIMING.RECORDING_START_OFFSET;
        
        return recordingStartTime;
    };

  const startShadowing = async () => {
  if (isNarratorFragmentBlocked) {
    return;
  }

  let activeStream = stream;

  if (!(activeStream instanceof MediaStream)) {
    activeStream = await getMicrophonePermission();
  }

  if (!(activeStream instanceof MediaStream) || !segment) {
    console.error("Cannot start shadowing: stream or segment missing");
    return;
  }

  activeStreamRef.current = activeStream;

  if (shadowingTimeout) {
    clearTimeout(shadowingTimeout);
    setShadowingTimeout(null);
  }

  if (cueTimeouts.length > 0) {
    cueTimeouts.forEach((timeout) => timeout && clearTimeout(timeout));
    setCueTimeouts([]);
  }

  let recordingStartTime = 0;

  if (segment.words && segment.words.length > 0) {
    recordingStartTime = triggerFirstWordCue();
  }

  setIsShadowing(true);

  const recordingTimeout = setTimeout(() => {
    setRecordingStatus("recording");

    try {
      const media = createRecorder(activeStream);

      mediaRecorder.current = media;
      mediaRecorderRef.current = media;

      setupRecorderHandlers(media);

      media.start();

      const audioDelay = SHADOWING_TIMING.AUDIO_START_DELAY - recordingStartTime;

      const playTimeout = setTimeout(() => {
        if (audioref.current && narratorFragmentAudioUrl && segment?.start !== undefined) {
          setFocus(segment);
          audioref.current.currentTime = 0;
          audioref.current.play();

          if (currentHandlerRef.current) {
            audioref.current.removeEventListener(
              "timeupdate",
              currentHandlerRef.current
            );
          }

          currentHandlerRef.current = handler;
          audioref.current.addEventListener("timeupdate", currentHandlerRef.current);
          setIsPlayingSegment(true);

          if (segment.start !== undefined && segment.end !== undefined) {
            const segmentDuration = (segment.end - segment.start) * 1000;
            const totalDuration =
              segmentDuration + SHADOWING_TIMING.POST_AUDIO_BUFFER;

            const stopTimeout = setTimeout(() => {
              if (mediaRecorder.current?.state === "recording") {
                stopShadowingRecording(true);
              }
            }, totalDuration);

            setShadowingTimeout(stopTimeout);
          }
        }
      }, Math.max(0, audioDelay));

      setCueTimeouts((prev) => [...prev, playTimeout]);
    } catch (error) {
      console.error("Error starting shadowing:", error);
      setIsShadowing(false);
      setRecordingStatus("inactive");
    }
  }, Math.max(0, recordingStartTime));

  setCueTimeouts((prev) => [...prev, recordingTimeout]);
};

    const stopShadowingRecording = (sendForAnalysis = true) => {
  if (sendForAnalysis) {
    setAssessment(null);
    setLoading(true);
  }

  setRecordingProgress(0);

  if (recordRef.current) {
    const animations = recordRef.current.getAnimations();
    if (animations) {
      animations.forEach((animation) => {
        if (animation && typeof animation.pause === "function") {
          animation.pause();
        }
      });
    }
  }

  setRecordingStatus("inactive");
  setIsShadowing(false);

  if (shadowingTimeout) {
    clearTimeout(shadowingTimeout);
    setShadowingTimeout(null);
  }

  if (cueTimeouts.length > 0) {
    cueTimeouts.forEach((timeout) => timeout && clearTimeout(timeout));
    setCueTimeouts([]);
  }

  setIsCueActive(false);
  setShowFirstWordCue(false);
  setCuePulseCount(0);

  if (!mediaRecorder.current || mediaRecorder.current.state === "inactive") {
    setIsShadowing(false);
    return;
  }

  const recorder = mediaRecorder.current;

  recorder.onstop = () => {
    if (sendForAnalysis) {
      const audioBlob = buildRecordingBlob(recorder);

      if (!audioBlob) {
        console.error("No shadowing recording blob created");
        setLoading(false);
        audioChunksRef.current = [];
        setAudioChunks([]);
        return;
      }

      const audioUrl = URL.createObjectURL(audioBlob);

      sendBlob({
        blob: audioBlob,
        subject,
        file,
        segment,
      });

      setAudio(audioUrl);
      setHasRecording(true);
    }

    audioChunksRef.current = [];
    setAudioChunks([]);
    setIsShadowing(false);
  };

  try {
    recorder.stop();
  } catch (error) {
    console.error("Error stopping shadow recording:", error);
    setLoading(false);
    setIsShadowing(false);
  }
};

    // ============================================
    // SECTION 6.7: ECHOING MODE FUNCTIONS - FIXED WITH SHORTER TIMING
    // ============================================
    
const startEchoingMode = async () => {
  if (isNarratorFragmentBlocked) {
    return;
  }

  let activeStream = stream;

  if (!(activeStream instanceof MediaStream)) {
    activeStream = await getMicrophonePermission();
  }

  if (!(activeStream instanceof MediaStream)) {
    return;
  }

  activeStreamRef.current = activeStream;

  cancelAllProcesses();

  setEchoingPhase("playing");
  echoingPhaseRef.current = "playing";

  if (audioref.current && narratorFragmentAudioUrl && segment && segment.hasOwnProperty("start")) {
    setFocus(segment);
    audioref.current.currentTime = 0;
    audioref.current.play();

    if (currentHandlerRef.current) {
      audioref.current.removeEventListener(
        "timeupdate",
        currentHandlerRef.current
      );
    }

    currentHandlerRef.current = handler;
    audioref.current.addEventListener("timeupdate", currentHandlerRef.current);
    setIsPlayingSegment(true);
  }
};

    const startEchoingCountdown = () => {
        setEchoingPhase('paused');
        echoingPhaseRef.current = 'paused';
        setEchoingPulseCount(0);
        
        // Clear any existing timeouts
        if (echoingTimeouts.length > 0) {
            echoingTimeouts.forEach(timeout => timeout && clearTimeout(timeout));
            setEchoingTimeouts([]);
        }
        
        const newTimeouts = [];
        
        // Pulse 1 - 300ms
        const timeout1 = setTimeout(() => {
            setEchoingPulseCount(1);
        }, ECHOING_TIMING.PULSE_1_TIME);
        newTimeouts.push(timeout1);
        
        // Pulse 2 - 900ms
        const timeout2 = setTimeout(() => {
            setEchoingPulseCount(2);
        }, ECHOING_TIMING.PULSE_2_TIME);
        newTimeouts.push(timeout2);
        
        // Pulse 3 - 1500ms, then hold briefly before recording
        const timeout3 = setTimeout(() => {
            setEchoingPulseCount(3);
            
            // Hold the completed cue for 500ms before recording
            const clearTimeoutId = setTimeout(() => {
                setEchoingPhase('recording');
                echoingPhaseRef.current = 'recording';
                startEchoingRecording();
            }, ECHOING_TIMING.CUE_CLEAR_TIME);
            newTimeouts.push(clearTimeoutId);
        }, ECHOING_TIMING.PULSE_3_TIME);
        newTimeouts.push(timeout3);
        
        setEchoingTimeouts(prev => [...prev, ...newTimeouts]);
    };

const startEchoingRecording = () => {
  const activeStream =
    stream instanceof MediaStream ? stream : activeStreamRef.current;

  if (!(activeStream instanceof MediaStream)) {
    console.error("Cannot start echoing recording: invalid stream", activeStream);
    setEchoingPhase("idle");
    echoingPhaseRef.current = "idle";
    setRecordingStatus("inactive");
    return;
  }

  setRecordingStatus("recording");
  setRecordingProgress(0);

  const maxTime = 3 * duration * 1000;
  setMaxRecordingTime(maxTime);

  const media = createRecorder(activeStream);

  mediaRecorder.current = media;
  mediaRecorderRef.current = media;

  setupRecorderHandlers(media);

  media.start();

  const stopTimeout = setTimeout(() => {
    if (
      mediaRecorder.current?.state === "recording" &&
      echoingPhaseRef.current === "recording"
    ) {
      stopEchoingRecording(true);
    }
  }, maxTime);

  setEchoingTimeout(stopTimeout);
};

 const stopEchoingRecording = (sendForAnalysis = true) => {
  if (sendForAnalysis) {
    setAssessment(null);
    setLoading(true);
  }

  setRecordingProgress(0);

  if (echoingTimeout) {
    clearTimeout(echoingTimeout);
    setEchoingTimeout(null);
  }

  if (echoingTimeouts.length > 0) {
    echoingTimeouts.forEach((timeout) => timeout && clearTimeout(timeout));
    setEchoingTimeouts([]);
  }

  setEchoingPhase("idle");
  echoingPhaseRef.current = "idle";
  setEchoingPulseCount(0);
  setRecordingStatus("inactive");

  if (!mediaRecorder.current || mediaRecorder.current.state === "inactive") {
    return;
  }

  const recorder = mediaRecorder.current;

  recorder.onstop = () => {
    if (sendForAnalysis) {
      const audioBlob = buildRecordingBlob(recorder);

      if (!audioBlob) {
        console.error("No echoing recording blob created");
        setLoading(false);
        audioChunksRef.current = [];
        setAudioChunks([]);
        return;
      }

      const audioUrl = URL.createObjectURL(audioBlob);

      sendBlob({
        blob: audioBlob,
        subject,
        file,
        segment,
      });

      setAudio(audioUrl);
      setHasRecording(true);
    }

    audioChunksRef.current = [];
    setAudioChunks([]);
  };

  try {
    recorder.stop();
  } catch (error) {
    console.error("Error stopping echo recording:", error);
    setLoading(false);
  }
};

    const cancelEchoingMode = () => {
        cancelAllProcesses();
        setEchoingPhase('idle');
        echoingPhaseRef.current = 'idle';
        setEchoingPulseCount(0);
    };

    // ============================================
    // SECTION 7: UI HANDLER FUNCTIONS
    // ============================================
    
    const handleWordClick = (word) => {
        if (!word || !subject || !subject.language) return;
        
        getTranslations({"word": word, "tolanguage": "English", "fromlanguage": subject.language});
        
        if (isMobile) {
  setShowTranslationModal(true);
} else {
  setSidebarCollapsed(false);
}
    };



    const handleSelectSegment = (targetIndex) => {
        if (!Array.isArray(segments) || segments.length === 0) {
            return;
        }

        const nextIndex = Math.min(
            segments.length - 1,
            Math.max(0, Math.trunc(Number(targetIndex) || 0))
        );

        const nextSegment = segments[nextIndex];
        const currentIndex = segments.findIndex(
            (item) =>
                item === segment ||
                (segment?.id != null && item?.id === segment.id)
        );

        if (!nextSegment || nextIndex === currentIndex) {
            return;
        }

        cancelAllProcesses();

        setAssessment(null);
        setLoading(false);
        setTranslation();

        if (audioref.current) {
            audioref.current.pause();
            audioref.current.currentTime = 0;
        }

        setSegment(nextSegment);
        onSentenceIndexChange?.(nextIndex);
        setCurrentWord(0);
    };

    const handlePreviousSegment = () => {
        if (!segment) return;
        const index = Array.isArray(segments)
            ? segments.findIndex(
                (item) =>
          item === segment ||
          (segment?.id != null && item?.id === segment.id)
              )
            : 0;
        handleSelectSegment(Math.max(0, index - 1));
    };

    const handleNextSegment = () => {
        if (!segment) return;
        const index = Array.isArray(segments)
            ? segments.findIndex(
                (item) =>
          item === segment ||
          (segment?.id != null && item?.id === segment.id)
              )
            : 0;
        handleSelectSegment(index + 1);
    };



   const handleWordPlay = (i, item) => {
  if (
    isNarratorFragmentBlocked ||
    !segment?.words?.length ||
    !item
  ) {
    return;
  }

  cancelAllProcesses();   // stop anything else
  setFocus(segment);
  setIsPlayingSegment(true);

  const startTime = i > 0 ? segment.words[i - 1].start : item.start;
  const endTime = item.end;

  playRangePrecise(startTime, endTime);
};

const handlePlayFromWord = (i, item) => {
  if (
    isNarratorFragmentBlocked ||
    !segment?.words?.length ||
    !item
  ) {
    return;
  }

  const startTime = Number(item.start);
  const lastWordEnd = Number(segment.words[segment.words.length - 1]?.end);
  const segmentEnd = Number(segment.end);
  const endTime = Number.isFinite(segmentEnd) ? segmentEnd : lastWordEnd;

  if (!Number.isFinite(startTime) || !Number.isFinite(endTime) || endTime <= startTime) {
    return;
  }

  cancelAllProcesses();
  setFocus(segment);
  setCurrentWord(i);
  setIsPlayingSegment(true);
  playRangePrecise(startTime, endTime);
};

const handleRangePlay = ({
  startIndex,
  endIndex,
}) => {
  if (
    isNarratorFragmentBlocked ||
    !segment?.words?.length
  ) {
    return;
  }

  cancelAllProcesses();
  setFocus(segment);
  setIsPlayingSegment(true);

  const actualStartIndex = Math.max(0, startIndex - 1);

  const startTime = segment.words[actualStartIndex].start;
  const endTime = segment.words[endIndex].end;

  playRangePrecise(startTime, endTime);
};
    // ============================================
    // SECTION 8: MAIN ACTION HANDLER
    // ============================================
    
    const handleMainAction = () => {
        if (practiceMode === 'writeEcho' && writeEchoStage !== 'echo') {
            return;
        }

        const isStartingEcho =
          isEchoPracticeMode && echoingPhase === 'idle';

        const isStartingShadowing =
          practiceMode === 'shadowing' &&
          !isShadowing &&
          recordingStatus === 'inactive';

        if (
          isNarratorFragmentBlocked &&
          (isStartingEcho || isStartingShadowing)
        ) {
          return;
        }

        switch (practiceMode) {
            case 'echoing':
            case 'writeEcho':
                if (echoingPhase === 'idle') {
                    startEchoingMode();
                } else if (echoingPhase === 'recording') {
                    stopEchoingRecording(true);
                } else {
                    cancelEchoingMode();
                }
                break;
                
            case 'shadowing':
                if (!isShadowing && recordingStatus === 'inactive') {
                    startShadowing();
                } else if (recordingStatus === 'recording') {
                    stopShadowingRecording(true);
                }
                break;
                
            case 'manual':
                if (recordingStatus === 'inactive') {
                    startRecording(true);
                } else {
                    stopRecording(true);
                }
                break;
        }
    };

    // ============================================
    // SECTION 9: KEYBOARD SHORTCUT HANDLERS
    // ============================================
    
    const handlePlayPause = () => {
        const audioElement = audioref.current;

        if (!audioElement || !segment) {
            return;
        }

        if (audioElement.paused) {
            if (isNarratorFragmentBlocked) {
                return;
            }

            setFocus(segment);
            audioElement.currentTime = 0;

            const playPromise = audioElement.play();
            if (
              playPromise &&
              typeof playPromise.catch === "function"
            ) {
              playPromise.catch((error) => {
                console.error(
                  "Could not play narrator fragment:",
                  error
                );
                setIsPlayingSegment(false);
              });
            }

            if (currentHandlerRef.current) {
                audioElement.removeEventListener(
                  "timeupdate",
                  currentHandlerRef.current
                );
            }

            currentHandlerRef.current = handler;
            audioElement.addEventListener(
              "timeupdate",
              currentHandlerRef.current
            );
            setIsPlayingSegment(true);
        } else {
            audioElement.pause();
            setIsPlayingSegment(false);
        }
    };

    const handleEscape = () => {
        cancelAllProcesses();
    };

    const cycleVisualizationView = () => {
        if (practiceMode === 'writeEcho') return;

        const currentIndex = VISIBLE_VISUALIZATION_VIEWS.indexOf(visualizationView);
        const safeCurrentIndex = currentIndex >= 0 ? currentIndex : 0;
        const nextIndex =
          (safeCurrentIndex + 1) % VISIBLE_VISUALIZATION_VIEWS.length;
        setVisualizationView(VISIBLE_VISUALIZATION_VIEWS[nextIndex]);
    };

    // ============================================
    // SECTION 10: VISUAL COMPONENTS
    // ============================================

    // Get button configuration based on mode and state
    const getButtonConfig = () => {
        const isStartingEcho =
          isEchoPracticeMode && echoingPhase === 'idle';

        const isStartingShadowing =
          practiceMode === 'shadowing' &&
          !isShadowing &&
          recordingStatus === 'inactive';

        if (
          isNarratorFragmentBlocked &&
          (isStartingEcho || isStartingShadowing)
        ) {
          return {
            text: isStartingShadowing
              ? 'Shadow & Record'
              : 'Start Echoing',
            color:
              'from-slate-300 to-slate-400 text-slate-600',
            icon: isStartingShadowing ? '🎭' : '🔄',
            disabled: true,
            title: narratorFragmentError
              ? 'Audio unavailable'
              : 'Preparing the narrator audio fragment'
          };
        }

        if (isEchoPracticeMode) {
            switch (echoingPhase) {
                case 'idle': 
                    return { 
                        text: 'Start Echoing', 
                        color: 'from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600',
                        icon: '🔄',
                        disabled: false
                    };
                case 'playing': 
                    return { 
                        text: 'Playing...', 
                        color: 'from-blue-400 to-indigo-400',
                        icon: '▶️',
                        disabled: true
                    };
                case 'paused': 
                    return { 
                        text: 'Get Ready...', 
                        color: 'from-amber-500 to-orange-500',
                        icon: '⏳',
                        disabled: true
                    };
                case 'recording': 
                    return { 
                        text: 'Stop Recording', 
                        color: 'from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600',
                        icon: '⏹️',
                        disabled: false
                    };
            }
        }
        
        if (practiceMode === 'shadowing') {
            if (isShadowing && recordingStatus === 'recording') {
                return { 
                    text: 'Stop Shadowing', 
                    color: 'from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600',
                    icon: '⏹️',
                    disabled: false
                };
            }
            return { 
                text: 'Shadow & Record', 
                color: 'from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600',
                icon: '🎭',
                disabled: recordingStatus !== 'inactive'            
              };
        }
        
        if (practiceMode === 'manual') {
            if (recordingStatus === 'recording') {
                return { 
                    text: 'Stop Recording', 
                    color: 'from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600',
                    icon: '⏹️',
                    disabled: false
                };
            }
            return { 
                text: 'Start Recording', 
                color: 'from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600',
                icon: '⏺️',
                disabled: !isgranted
            };
        }
        
        // Default
        return { 
            text: 'Start', 
            color: 'from-blue-500 to-indigo-500',
            icon: '▶️',
            disabled: false
        };
    };

  
    
// ================================
// HELP SYSTEM (metadata-controlled)
// ================================
const helpCfg = course?.help ?? course?.metadata?.help ?? {
  enabled: false,
  mode: "none", // "exercise" | "about" | "none"
};

const helpEnabled = !!helpCfg?.enabled;
const helpMode = helpCfg?.mode || "none";

const currentContextItem =
  helpEnabled &&
  helpMode === "context" &&
  Array.isArray(course?.context) &&
  file?.file
    ? course.context.find((item) => {
        const localPath = item?.local_audio_path || "";
        const localBase = String(localPath).split("/").pop();
        return localBase === file.file;
      }) || null
    : null;

const hasContextHelp = helpMode === "context" && !!currentContextItem;

// Derive exercise id (1A / 2B etc)
const exerciseId = deriveExerciseId(subject, file);

// Exercise lookup (only when in exercise mode)
const currentExercise =
  helpEnabled &&
  helpMode === "exercise" &&
  exerciseId &&
  Array.isArray(course?.exercises)
    ? course.exercises.find(
        (e) => String(e.id).toUpperCase() === String(exerciseId).toUpperCase()
      ) || null
    : null;

const hasExerciseHelp = helpMode === "exercise" && !!currentExercise;

// About-only mode
const isMetadataNoHelp = helpEnabled && helpMode === "about";
const isAboutOnly = helpEnabled && helpMode === "about";

// Header title / action label
const headerTitle =
  helpMode === "context"
    ? (currentContextItem?.place || sectionTitle || "Dialect sample")
    : isMetadataNoHelp
      ? (sectionTitle || "Exercise")
      : (exerciseId ? `Exercise ${exerciseId}` : (sectionTitle || "Exercise"));

const helpActionLabel =
  helpMode === "context"
    ? (helpCfg?.buttonLabel || "Dialect info")
    : isMetadataNoHelp
      ? "About course"
      : (helpCfg?.buttonLabel || "What to focus on");

// Current sentence is identified by its array index, not by segment.id.
const currentSentenceIndex = Array.isArray(segments)
  ? Math.max(
      0,
      segments.findIndex(
        (item) =>
          item === segment ||
          (segment?.id != null && item?.id === segment.id)
      )
    )
  : 0;

const subjectShareId = getSubjectShareId(subject);

const copySentenceShareLink = () => {
  if (!subjectShareId || !Array.isArray(segments) || segments.length === 0) {
    setSentenceShareStatus("failed");
    setTimeout(() => setSentenceShareStatus(null), 1400);
    return;
  }

  const safePartIndex = Math.max(0, Math.trunc(Number(partIndex) || 0));
  const safeSentenceIndex = Math.min(
    segments.length - 1,
    Math.max(0, currentSentenceIndex)
  );
  const url =
    `${window.location.origin}/subject/${encodeURIComponent(String(subjectShareId))}/` +
    `${safePartIndex}/${safeSentenceIndex}`;

  const mark = (status) => {
    setSentenceShareStatus(status);
    setTimeout(() => setSentenceShareStatus(null), 1400);
  };

  const fallbackCopy = () => {
    try {
      const textarea = document.createElement("textarea");
      textarea.value = url;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.select();
      const copied = document.execCommand("copy");
      textarea.remove();
      mark(copied ? "copied" : "failed");
    } catch (error) {
      console.error("Sentence share fallback failed:", error);
      mark("failed");
    }
  };

  if (navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(url).then(
      () => mark("copied"),
      fallbackCopy
    );
  } else {
    fallbackCopy();
  }
};

// Center label
const sentenceLabel = Array.isArray(segments) && segments.length
  ? `Sentence ${currentSentenceIndex + 1}/${segments.length}`
  : "Sentence";

// Cues
const showShadowCue =
  practiceMode === "shadowing" &&
  (isCueActive || recordingStatus === "recording");

const showEchoCue =
  isEchoPracticeMode && echoingPhase === "paused";

const showCenterCue = showShadowCue || showEchoCue;


const introAnchorTitle = currentExercise?.studentAnchor?.title || "";
const introAnchorSubtitle = currentExercise?.studentAnchor?.subtitle || "";
const introPattern =
  Array.isArray(currentExercise?.patterns) && currentExercise.patterns.length
    ? currentExercise.patterns[0]
    : "";

const canRunHelpIntro =
  helpEnabled &&
  helpMode === "exercise" &&
  !!currentExercise &&
  !!introAnchorTitle;


// ============================================
    // SECTION 11: EFFECTS
    // ============================================
    
useEffect(() => {
  return () => {
    if (protectedAssessmentAudioUrl) {
      URL.revokeObjectURL(protectedAssessmentAudioUrl);
    }
    if (protectedNarratorAudioUrl) {
      URL.revokeObjectURL(protectedNarratorAudioUrl);
    }
  };
}, [protectedAssessmentAudioUrl, protectedNarratorAudioUrl]);

useEffect(() => {
  fetchCommentCounts();
}, [
  subject?.owner,
  subject?.language,
  subject?.author,
  subject?.title,
  subject?.type,
  file?.file,
  segment?.start,
  segment?.end,
  segment?.text,
]);

useEffect(() => {
  let cancelled = false;
  let oldAssessmentBlob = protectedAssessmentAudioUrl;
  let oldNarratorBlob = protectedNarratorAudioUrl;

  const loadProtectedAudio = async () => {
    if (!assessment) {
      if (oldAssessmentBlob) URL.revokeObjectURL(oldAssessmentBlob);
      if (oldNarratorBlob) URL.revokeObjectURL(oldNarratorBlob);

      if (!cancelled) {
        setProtectedAssessmentAudioUrl(null);
        setProtectedNarratorAudioUrl(null);
      }
      return;
    }

    const userAudioPath =
      assessment?.audio_url ||
      assessment?.intonation?.audio_url ||
      null;

    const narratorAudioPath =
      assessment?.narrator_intonation?.audio_url ||
      null;

    let nextAssessmentBlob = null;
    let nextNarratorBlob = null;

    if (userAudioPath && userAudioPath.includes("/audio/private/")) {
      nextAssessmentBlob = await fetchProtectedAudioBlobUrl(userAudioPath);
    }

    if (narratorAudioPath && narratorAudioPath.includes("/audio/private/")) {
      nextNarratorBlob = await fetchProtectedAudioBlobUrl(narratorAudioPath);
    }

    if (cancelled) {
      if (nextAssessmentBlob) URL.revokeObjectURL(nextAssessmentBlob);
      if (nextNarratorBlob) URL.revokeObjectURL(nextNarratorBlob);
      return;
    }

    if (oldAssessmentBlob) URL.revokeObjectURL(oldAssessmentBlob);
    if (oldNarratorBlob) URL.revokeObjectURL(oldNarratorBlob);

    setProtectedAssessmentAudioUrl(nextAssessmentBlob);
    setProtectedNarratorAudioUrl(nextNarratorBlob);
  };

  loadProtectedAudio();

  return () => {
    cancelled = true;
  };
}, [assessment]);
    useEffect(() => {
  const key = subject?.course_key;

  // No course_key => no help system
  if (!key) {
    setCourse(null);
    return;
  }

  let alive = true;


  loadCourse(key)
    .then((c) => {
      if (!alive) return;
      setCourse(c);
    })
    .catch((err) => {
      console.warn("loadCourse failed:", err);
      if (!alive) return;
      setCourse(null);
    })
    .finally(() => {
      if (!alive) return;
    
    });

  return () => {
    alive = false;
  };
}, [subject?.course_key]);

useEffect(() => {
  if (!canRunHelpIntro) {
    setShowHelpIntro(false);
    return;
  }

  const introKey = `${subject?.course_key || ""}__${exerciseId || ""}`;

  if (lastIntroKeyRef.current === introKey) return;
  lastIntroKeyRef.current = introKey;

  setShowHelpIntro(true);

  if (helpIntroTimerRef.current) {
    clearTimeout(helpIntroTimerRef.current);
  }

  helpIntroTimerRef.current = setTimeout(() => {
    setShowHelpIntro(false);
    helpIntroTimerRef.current = null;
  }, 2000);

  return () => {
    if (helpIntroTimerRef.current) {
      clearTimeout(helpIntroTimerRef.current);
      helpIntroTimerRef.current = null;
    }
  };
}, [canRunHelpIntro, exerciseId, subject?.course_key]);

useEffect(() => {
  if (!showHelpIntro) return;

  const userIsActive =
    isPlayingSegment ||
    recordingStatus === "recording" ||
    isShadowing ||
    (isEchoPracticeMode && echoingPhase !== "idle");

  if (userIsActive) {
    setShowHelpIntro(false);
    if (helpIntroTimerRef.current) {
      clearTimeout(helpIntroTimerRef.current);
      helpIntroTimerRef.current = null;
    }
  }
}, [
  showHelpIntro,
  isPlayingSegment,
  recordingStatus,
  isShadowing,
  practiceMode,
  echoingPhase,
]);

    useEffect(() => {
  if (!audio) return;
  audioRecord.current = new Audio(audio);
  return () => {
    if (audioRecord.current) {
      audioRecord.current.pause();
      audioRecord.current = null;
    }
  };
}, [audio]);

   useEffect(() => {
  if (!isPublic && subject?.language) fetchDailyGoal(subject.language);
}, [isPublic, subject?.language]);

    useEffect(() => {
        setSegments(null);
        setSegment({});
        setAssessment(null);
        setAudio(null);
        setRecordingStatus("inactive");
        setAudioChunks([]);
        setHasRecording(false);
        setLoading(false);
        setTranslation(null);
        setCurrentWord(0);
        setBroadcastTime(0);
        setIsPlayingSegment(false);
        
        setIsShadowing(false);
        setShowFirstWordCue(false);
        setCuePulseCount(0);
        setIsCueActive(false);
        setRecordingProgress(0);
        setMaxRecordingTime(0);
        setEchoingPhase('idle');
        echoingPhaseRef.current = 'idle';
        setEchoingPulseCount(0);
        
        if (shadowingTimeout) {
            clearTimeout(shadowingTimeout);
            setShadowingTimeout(null);
        }
        
        if (echoingTimeout) {
            clearTimeout(echoingTimeout);
            setEchoingTimeout(null);
        }
        
        if (cueTimeouts.length > 0) {
            cueTimeouts.forEach(timeout => timeout && clearTimeout(timeout));
            setCueTimeouts([]);
        }
        
        if (echoingTimeouts.length > 0) {
            echoingTimeouts.forEach(timeout => timeout && clearTimeout(timeout));
            setEchoingTimeouts([]);
        }
        
        if (file && file.file) {
            getSegments({"subject": subject, "file": file.file});
        }
        
        if (audioRecordRef.current) {
            audioRecordRef.current.pause();
            audioRecordRef.current = null;
        }
    }, [
      file?.file,
      subject?.owner,
      subject?.language,
      subject?.author,
      subject?.title,
      subject?.type,
    ]);

    useEffect(() => {
        if (navigator) {
            navigator.permissions
                .query({ name: "microphone" })
                .then(async function (result) {
                    if (result.state === "granted") {
                        if ("MediaRecorder" in window) {
                            try {
                                const streamData = await navigator.mediaDevices.getUserMedia({
                                    audio: true,
                                    video: false,
                                });
                                setIsGranted(true);
                                setStream(streamData);
                            } catch (err) {
                                alert(err.message);
                            }
                        } else {
                            alert("The MediaRecorder API is not supported in your browser.");
                        }
                   } else if (result.state === "prompt") {
                    } else if (result.state === "denied") {
                        // Handle denied case
                    }
                });
        } else {
        }
    }, []);

    useEffect(() => {
     
        if (Array.isArray(segments) && segments.length > 0) {
            const requestedIndex = Number(initialSentenceIndex);
            const normalizedIndex = Number.isFinite(requestedIndex)
                ? Math.max(0, Math.trunc(requestedIndex))
                : 0;
            const resolvedIndex = Math.min(segments.length - 1, normalizedIndex);
            const resolvedSegment = segments[resolvedIndex];

            setSegment((current) =>
              current === resolvedSegment ? current : resolvedSegment
            );

            // Prop-driven initialization must not echo a valid route back to the
            // parent. Only report when an invalid/out-of-range index was clamped.
            if (resolvedIndex !== normalizedIndex) {
              onSentenceIndexChangeRef.current?.(resolvedIndex);
            }
        };  
        setAssessment(null); 
        setRate(1); 
        setLoading(false); 
        setTranslation();
        setIsPlayingSegment(false);
        setCurrentWord(0);
        setIsShadowing(false);
        setShowFirstWordCue(false);
        setCuePulseCount(0);
        setIsCueActive(false);
        setRecordingProgress(0);
        setMaxRecordingTime(0);
        setEchoingPhase('idle');
        echoingPhaseRef.current = 'idle';
        setEchoingPulseCount(0);
        
        setPracticeMode(preservedMode);
        
        if (shadowingTimeout) {
            clearTimeout(shadowingTimeout);
            setShadowingTimeout(null);
        }
        
        if (echoingTimeout) {
            clearTimeout(echoingTimeout);
            setEchoingTimeout(null);
        }
        
        if (cueTimeouts.length > 0) {
            cueTimeouts.forEach(timeout => timeout && clearTimeout(timeout));
            setCueTimeouts([]);
        }
        
        if (echoingTimeouts.length > 0) {
            echoingTimeouts.forEach(timeout => timeout && clearTimeout(timeout));
            setEchoingTimeouts([]);
        }
    }, [
      file?.file,
      subject?.owner,
      subject?.language,
      subject?.author,
      subject?.title,
      subject?.type,
      segments,
      initialSentenceIndex,
    ]);

    

    useEffect(() => {
  if (!isPublic && subject?.language) fetchTodayActivity(subject.language);
}, [isPublic, subject?.language]);

    useEffect(() => {
       
        if (segment) {
            if ( segment.hasOwnProperty("end") && segment.hasOwnProperty("start")) { 
                if (segment.end - segment.start > 0) {
                    setDuration(segment.end - segment.start);
                }
            }
        }
    }, [segment]);
    
    useEffect(() => {
  if (recordingStatus !== "recording") return;

  let rafId;
  let stopTimer;

  const preAudioBuffer = Math.abs(SHADOWING_TIMING.RECORDING_START_OFFSET);
  const postAudioBuffer = SHADOWING_TIMING.POST_AUDIO_BUFFER;
  const extraTime =
    practiceMode === "shadowing" ? preAudioBuffer + postAudioBuffer : 0;

  const totalDuration = 3 * duration * 1000 + extraTime;
  const startedAt = performance.now();

  setRecordingProgress(0);

  const tick = (now) => {
    const elapsed = now - startedAt;
    const nextProgress = Math.min((elapsed / totalDuration) * 100, 100);

    setRecordingProgress(nextProgress);

    if (nextProgress < 100) {
      rafId = requestAnimationFrame(tick);
    }
  };

  rafId = requestAnimationFrame(tick);

  stopTimer = setTimeout(() => {
    if (recordingStatus === "recording") {
      if (isEchoPracticeMode) {
        stopEchoingRecording(true);
      } else if (practiceMode === "shadowing") {
        stopShadowingRecording(true);
      } else {
        stopRecording(true);
      }
    }
  }, totalDuration);

  return () => {
    cancelAnimationFrame(rafId);
    clearTimeout(stopTimer);
  };
}, [recordingStatus, duration, practiceMode]);
    useEffect(() => {
        if (recordingStatus === "inactive") {
            setRecordingProgress(0);
        }
    }, [recordingStatus]);

    // Update echoingPhaseRef when echoingPhase changes
    useEffect(() => {
        echoingPhaseRef.current = echoingPhase;
    }, [echoingPhase]);

    // Keyboard Navigation Effect
  useEffect(() => {
    const handleKeyPress = (event) => {
        if (showExerciseHelp) return;
        if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA') {
            return;
        }

        switch (event.key) {
            case 'ArrowLeft':
            case 'a':
            case 'A':
                event.preventDefault();
                handlePreviousSegment();
                break;

            case 'ArrowRight':
            case 'd':
            case 'D':
                event.preventDefault();
                handleNextSegment();
                break;

            case ' ':
                event.preventDefault();
                handlePlayPause();
                break;

            case 'r':
            case 'R':
                event.preventDefault();
                handleMainAction(); // R now works for ALL modes
                break;

            case 'p':
            case 'P':
                event.preventDefault();
                if (hasRecording && audio) {
                    Play_record();
                }
                break;

            case 'Escape':
                event.preventDefault();
                handleEscape();
                break;

            case '?':
                event.preventDefault();
                setShowKeyboardHelp(prev => !prev);
                break;

            case '1':
                event.preventDefault();
                setRate(1);
                if (audioref.current) audioref.current.playbackRate = 1;
                break;

            case '2':
                event.preventDefault();
                setRate(0.5);
                if (audioref.current) audioref.current.playbackRate = 0.5;
                break;

            case '3':
                event.preventDefault();
                setRate(0.25);
                if (audioref.current) audioref.current.playbackRate = 0.25;
                break;

            case 'm':
            case 'M':
                event.preventDefault();
                setShowIntonationVisualization(prev => !prev);
                break;

            case 'v':
            case 'V':
                event.preventDefault();
                cycleVisualizationView();
                break;

            case 't':
            case 'T':
                event.preventDefault();
                // Cycle through all practice modes.
                const modes = ['echoing', 'writeEcho', 'shadowing', 'manual'];
                const currentIndex = modes.indexOf(practiceMode);
                const nextIndex = (currentIndex + 1) % modes.length;
                const nextMode = modes[nextIndex];
                cancelAllProcesses();
                setPracticeMode(nextMode);
                setPreservedMode(nextMode);
                if (nextMode === 'writeEcho') {
                    setWriteEchoStage('write');
                    setViewMode('practice');
                    setVisualizationView('pitch');
                }
                break;
        }
    };

    keyboardListenerRef.current = handleKeyPress;
    document.addEventListener('keydown', handleKeyPress);
    return () => {
        document.removeEventListener('keydown', handleKeyPress);
    };
}, [segment, segments, isgranted, recordingStatus, hasRecording, audio, visualizationView, activeTab, handler, practiceMode, echoingPhase, showExerciseHelp]);
    // Close mobile menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (mobileMenuOpen && !event.target.closest('.mobile-menu') && !event.target.closest('.hamburger-button')) {
                setMobileMenuOpen(false);
            }
            if (showKeyboardHelp && !event.target.closest('.keyboard-help') && !event.target.closest('.keyboard-help-button')) {
                setShowKeyboardHelp(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [mobileMenuOpen, showKeyboardHelp]);

    // Cleanup timeouts on unmount
    useEffect(() => {
        return () => {
            cancelAllProcesses();
            
            if (audioRecordRef.current) {
                audioRecordRef.current.pause();
                audioRecordRef.current = null;
            }
            
            if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
                try {
                    mediaRecorderRef.current.stop();
                } catch (error) {
                    console.error("Error cleaning up media recorder:", error);
                }
            }
            
            if (audioref.current) {
                if (currentHandlerRef.current) {
                    audioref.current.removeEventListener('timeupdate', currentHandlerRef.current);
                }
                
            }
            
            if (keyboardListenerRef.current) {
                document.removeEventListener('keydown', keyboardListenerRef.current);
            }
        };
    }, []);

    // ============================================
    // SECTION 12: MAIN RENDER
    // ============================================
    return (
     <div className="relative flex flex-col xl:flex-row h-[100dvh] w-full overflow-hidden bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-100/30">

            {/* Keyboard Help Modal */}
            <KeyboardHelpModal 
                showKeyboardHelp={showKeyboardHelp} 
                setShowKeyboardHelp={setShowKeyboardHelp} 
            />

            {viewMode === "presentation" && (
              <div className="absolute right-4 top-4 z-[70]">
                {renderSentenceSharePill()}
              </div>
            )}
            
           

            {/* Desktop Sidebar */}
<div
  className={[
    "hidden xl:flex flex-none flex-col gap-4 transition-all duration-300 h-full min-h-0 overflow-y-auto",
    "bg-white/20 backdrop-blur-sm border-r border-white/30",
sidebarCollapsed ? "xl:w-28 items-center px-4 py-4" : "xl:w-96 p-4",  ].join(" ")}
>
 {sidebarCollapsed ? (
  <>
    {/* Expand */}
    <button
      onClick={() => setSidebarCollapsed(false)}
      className="
        group relative mt-1
        w-14 h-14 rounded-2xl
        bg-gradient-to-br from-blue-600 to-indigo-600
        text-white shadow-xl shadow-blue-500/25
        hover:scale-105 active:scale-95
        transition-all flex items-center justify-center
      "
      title="Expand sidebar"
    >
      <BsArrowsExpand className="text-[26px] rotate-90" />

      <span className="absolute left-full ml-3 px-3 py-2 rounded-xl bg-slate-900 text-white text-xs font-semibold whitespace-nowrap opacity-0 group-hover:opacity-100 transition pointer-events-none">
        Expand sidebar
      </span>
    </button>

    <div className="w-8 h-px bg-slate-300 my-2" />

    {/* Daily progress */}
    {!isPublic && (() => {
      const pct = Math.min(
        100,
        Math.max(
          0,
          Math.round((todaySeconds / Math.max(goalSeconds || 1, 1)) * 100)
        )
      );

      const radius = 18;
      const circumference = 2 * Math.PI * radius;
      const dashOffset = circumference - (pct / 100) * circumference;

      const tone =
        pct < 35
          ? {
              label: "Warming up",
              border: "border-orange-200",
              bg: "bg-orange-50",
              dot: "bg-orange-400",
              id: "warm",
              stops: ["#f59e0b", "#f97316"],
            }
          : pct < 75
          ? {
              label: "On track",
              border: "border-blue-200",
              bg: "bg-blue-50",
              dot: "bg-blue-400",
              id: "track",
              stops: ["#3b82f6", "#6366f1"],
            }
          : {
              label: "Goal crushing",
              border: "border-emerald-200",
              bg: "bg-emerald-50",
              dot: "bg-emerald-400",
              id: "crush",
              stops: ["#10b981", "#14b8a6"],
            };

      return (
        <button
          onClick={() => setShowProgressModal(true)}
          className={`
            group relative
            w-16 h-16 min-w-16 min-h-16 shrink-0 aspect-square p-0 rounded-3xl
            bg-white border ${tone.border}
            shadow-sm hover:shadow-xl
            hover:-translate-y-0.5
            transition-all
            flex items-center justify-center
            ${pct >= 75 ? "shadow-emerald-500/10" : ""}
          `}
          title={`${tone.label} · ${pct}%`}
        >
          <div className={`absolute inset-1 rounded-[1.3rem] ${tone.bg}`} />

          {pct >= 75 && (
            <div className="absolute inset-0 rounded-3xl bg-emerald-400/10 animate-pulse" />
          )}

          <svg
            className="absolute inset-0 m-auto block w-14 h-14 min-w-14 min-h-14 shrink-0 -rotate-90"
            style={{ aspectRatio: "1 / 1" }}
            viewBox="0 0 44 44"
            preserveAspectRatio="xMidYMid meet"
          >
            <circle
              cx="22"
              cy="22"
              r={radius}
              fill="none"
              stroke="rgba(148,163,184,.22)"
              strokeWidth="4"
            />

            <circle
              cx="22"
              cy="22"
              r={radius}
              fill="none"
              stroke={`url(#${tone.id})`}
              strokeWidth="4"
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={dashOffset}
              className="transition-all duration-700 ease-out"
            />

            <defs>
              <linearGradient id={tone.id}>
                <stop offset="0%" stopColor={tone.stops[0]} />
                <stop offset="100%" stopColor={tone.stops[1]} />
              </linearGradient>
            </defs>
          </svg>

          <div className="relative">
            <span className="text-[24px] leading-none drop-shadow-sm">
              {getLanguageFlag(subject?.language)}
            </span>

            <span
              className={`
                absolute -top-1 -right-1
                w-2.5 h-2.5 rounded-full
                ring-2 ring-white shadow-sm
                ${tone.dot}
              `}
            />
          </div>

          <span className="absolute left-full ml-3 top-1/2 -translate-y-1/2 px-3 py-2 rounded-xl bg-slate-900 text-white text-xs font-semibold whitespace-nowrap opacity-0 group-hover:opacity-100 transition pointer-events-none shadow-lg">
            {tone.label} · {pct}%
          </span>
        </button>
      );
    })()}

    {/* Practice modes */}
    {[
      {
        mode: "echoing",
        label: "Echo Mode",
        shortcut: "R",
        activeColor: "bg-gradient-to-br from-blue-500 to-indigo-600",
        icon:
          practiceMode === "echoing" && recordingStatus === "recording"
            ? "🔴"
            : practiceMode === "echoing" && echoingPhase === "playing"
            ? "▶️"
            : "🔄",
      },
      {
        mode: "writeEcho",
        label: "Write & Echo",
        shortcut: "R",
        activeColor: "bg-gradient-to-br from-violet-500 to-indigo-600",
        icon:
          practiceMode === "writeEcho" && recordingStatus === "recording"
            ? "🔴"
            : practiceMode === "writeEcho" && echoingPhase === "playing"
            ? "▶️"
            : "✍️",
      },
      {
        mode: "shadowing",
        label: "Shadow Mode",
        shortcut: "R",
        activeColor: "bg-gradient-to-br from-amber-500 to-orange-600",
        icon:
          practiceMode === "shadowing" && recordingStatus === "recording"
            ? "🔴"
            : practiceMode === "shadowing" && isPlayingSegment
            ? "▶️"
            : "🎭",
      },
      {
        mode: "manual",
        label: "Manual Mode",
        shortcut: "R",
        activeColor: "bg-gradient-to-br from-emerald-500 to-green-600",
        icon: practiceMode === "manual" && recordingStatus === "recording" ? "🔴" : "🎮",
      },
    ].map(({ mode, icon, label, shortcut, activeColor }) => {
      const active = practiceMode === mode;

      return (
        <button
          key={mode}
          onClick={() => {
            cancelAllProcesses();
            setPracticeMode(mode);
            setPreservedMode(mode);
            if (mode === "writeEcho") {
              setWriteEchoStage("write");
              setViewMode("practice");
              setVisualizationView("pitch");
            }
          }}
          className="group relative"
          title={label}
        >
          {active && (
            <span className="absolute -left-3 top-1/2 -translate-y-1/2 w-1 h-7 rounded-full bg-blue-500 shadow-sm" />
          )}

          <div
            className={[
              "relative w-12 h-12 rounded-xl flex items-center justify-center border transition-all",
              "hover:-translate-y-0.5",
              active
                ? `${activeColor} text-white border-transparent shadow-lg`
                : "bg-white border-slate-200 hover:bg-slate-50 shadow-sm",
            ].join(" ")}
          >
            <span className={active && recordingStatus === "recording" ? "animate-pulse" : ""}>
              {icon}
            </span>
          </div>

          <span className="absolute left-full ml-3 top-1/2 -translate-y-1/2 px-3 py-2 rounded-xl bg-slate-900 text-white text-xs font-semibold whitespace-nowrap opacity-0 group-hover:opacity-100 transition pointer-events-none shadow-lg">
            {label} · Press {shortcut}
          </span>
        </button>
      );
    })}

    <div className="w-8 h-px bg-slate-300 my-2" />

    {/* Visualization modes */}
    {[
      {
        key: "pitch",
        icon: "📈",
        label: "Pitch View",
        color: "text-blue-600",
        active: "bg-gradient-to-br from-blue-500 to-cyan-500",
      },
      {
        key: "music",
        icon: "🎵",
        label: "Music Notes",
        color: "text-violet-600",
        active: "bg-gradient-to-br from-purple-500 to-pink-500",
      },
      {
        key: "combined",
        icon: "🎛️",
        label: "Pitch + loudness",
        color: "text-indigo-600",
        active: "bg-gradient-to-br from-indigo-500 to-purple-500",
      },
    ].map(({ key, icon, label, color, active }) => {
      const selected = visualizationView === key;

      return (
        <button
          key={key}
          onClick={() => setVisualizationView(key)}
          title={label}
          className="group relative"
        >
          {selected && (
            <span className="absolute -left-3 top-1/2 -translate-y-1/2 w-1 h-7 rounded-full bg-purple-500 shadow-sm" />
          )}

          <div
            className={[
              "w-12 h-12 rounded-xl flex items-center justify-center border transition-all",
              "hover:-translate-y-0.5",
              selected
                ? `${active} text-white border-transparent shadow-lg`
                : `bg-white border-slate-200 hover:bg-slate-50 shadow-sm ${color}`,
            ].join(" ")}
          >
            {icon}
          </div>

          <span className="absolute left-full ml-3 top-1/2 -translate-y-1/2 px-3 py-2 rounded-xl bg-slate-900 text-white text-xs font-semibold whitespace-nowrap opacity-0 group-hover:opacity-100 transition pointer-events-none shadow-lg">
            {label} · Press V
          </span>
        </button>
      );
    })}

    <div className="flex-1 min-h-8" />

    {/* Keyboard shortcuts */}
   <button
  onClick={() => setShowKeyboardHelp(true)}
  className="
    mt-auto mb-5
    group relative
    w-14 h-14 rounded-2xl
    bg-gradient-to-br from-slate-700 to-slate-900
    text-white
    shadow-xl shadow-slate-500/20
    hover:shadow-2xl hover:-translate-y-0.5
    hover:scale-[1.03]
    active:scale-95
    transition-all duration-300
    flex items-center justify-center
    ring-1 ring-white/10
  "
  title="Keyboard shortcuts (?)"
>
  <MdKeyboard className="text-[24px] group-hover:scale-110 transition-transform duration-300" />

  {/* quiet by default, pulses only on hover */}
  <span
    className="
      absolute top-2 right-2
      w-2 h-2 rounded-full
      bg-cyan-400
      opacity-70
      shadow-[0_0_8px_rgba(34,211,238,.35)]
      group-hover:animate-pulse
      group-hover:opacity-100
    "
  />

  {/* tooltip */}
  <span
    className="
      absolute left-full ml-3
      px-3 py-2 rounded-xl
      bg-slate-900 text-white text-xs font-semibold
      whitespace-nowrap
      opacity-0 group-hover:opacity-100
      transition pointer-events-none
      shadow-xl
    "
  >
    Keyboard Shortcuts · press ?
  </span>
</button>
  </>
) : (
    <>
      {/* Translation Panel - Desktop */}
      {translation && (
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-5 border border-white/40 shadow-lg shadow-blue-500/5 flex-shrink-0">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-800 flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-amber-500 to-orange-500 rounded-lg flex items-center justify-center text-white text-sm">
                🌐
              </div>
              Translation
            </h3>
            <button
              onClick={() => setTranslation()}
              className="p-2 hover:bg-white/60 rounded-xl transition-all duration-200 border border-white/40"
            >
              <span className="text-gray-500 text-lg">×</span>
            </button>
          </div>

          {translation.translation[0]?.hasOwnProperty("normalizedSource") ? (
            <div className="text-left space-y-3">
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-3 border border-blue-200/50">
                <p className="font-semibold text-gray-800">
                  {translation.translation[0].normalizedSource}
                </p>
                <p className="text-xs text-gray-500 mt-1">normalized form</p>
              </div>
              <ul className="space-y-2 text-sm">
                {translation.translation[0].translations.map((item, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-3 p-2 rounded-lg bg-white/50 border border-white/40"
                  >
                    <span className="font-bold text-blue-500 bg-blue-100 rounded-lg px-2 py-1 text-xs">
                      {i + 1}
                    </span>
                    <span className="text-gray-700 flex-1">
                      {item.displayTarget}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ) : translation.translation === "missing translation" ? (
            <div className="text-center text-gray-500 py-4 bg-white/50 rounded-xl border border-white/40">
              <div className="text-2xl mb-2">🔍</div>
              <p>Translation not available</p>
            </div>
          ) : (
            <div className="text-left space-y-3">
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-3 border border-green-200/50">
                <p className="font-semibold text-gray-800">
                  {translation.word}
                </p>
              </div>
              <ul className="space-y-2 text-sm">
                {translation.translation[0].translations.map((item, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-3 p-2 rounded-lg bg-white/50 border border-white/40"
                  >
                    <span className="font-bold text-green-500 bg-green-100 rounded-lg px-2 py-1 text-xs">
                      {i + 1}
                    </span>
                    <span className="text-gray-700 flex-1">{item.text}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {!isPublic && (
        <div className="flex justify-center">
          <ProgressPill
            language={subject?.language || "en"}
            showLanguageFlag={true}
            doneSeconds={todaySeconds}
            goalSeconds={goalSeconds}
            onClick={() => setShowProgressModal(true)}
            collapsed={false}
          />
        </div>
      )}

      {/* Practice Mode Panel */}
      <div className="relative flex-shrink-0 overflow-hidden rounded-[24px] border border-slate-200/90 bg-white/95 p-4 shadow-[0_22px_55px_-38px_rgba(15,23,42,0.45)] backdrop-blur-xl">
        <div className="pointer-events-none absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-blue-500 via-indigo-500 to-violet-500" />

        <div className="mb-4 flex items-center justify-between gap-3 pt-1">
          <div className="min-w-0">
            <div className="flex items-center gap-2.5">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-slate-900 text-base text-white shadow-sm">
                🎵
              </div>

              <div className="min-w-0">
                <h2 className="truncate text-base font-bold tracking-tight text-slate-950">
                  Practice mode
                </h2>
                <div className="mt-0.5 flex items-center gap-1.5 text-[11px] font-semibold text-slate-500">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                  <span className="truncate">
                    {practiceMode === "writeEcho"
                      ? "Write & Echo"
                      : practiceMode === "shadowing"
                      ? "Shadow Mode"
                      : practiceMode === "manual"
                      ? "Manual Mode"
                      : "Echo Mode"}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={() => setSidebarCollapsed(true)}
            aria-label="Collapse sidebar"
            title="Collapse sidebar"
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-slate-50 text-slate-600 shadow-sm transition hover:-translate-y-0.5 hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700 hover:shadow-md active:translate-y-0"
          >
            <BsArrowsCollapse className="rotate-90 text-xl" />
          </button>
        </div>

        <div className="mb-4 min-w-0">
          <PresentationModeToggle
            presentationMode={viewMode === "presentation"}
            setPresentationMode={(next) =>
              setViewMode(next ? "presentation" : "practice")
            }
            disabled={practiceMode === "writeEcho"}
          />
        </div>

        <div className="space-y-2">
          {[
            {
              key: "echoing",
              label: "Echo Mode",
              icon: "🔄",
              desc: "Listen, then repeat",
              gradient: "linear-gradient(135deg, #2563eb 0%, #4f46e5 100%)",
              tint: "#eff6ff",
              border: "#bfdbfe",
              iconTint: "#dbeafe",
              accent: "#2563eb",
              shadow: "0 16px 32px -20px rgba(37, 99, 235, 0.9)",
            },
            {
              key: "writeEcho",
              label: "Write & Echo",
              icon: "✍️",
              desc: "Listen, write, check, echo",
              gradient: "linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)",
              tint: "#f5f3ff",
              border: "#ddd6fe",
              iconTint: "#ede9fe",
              accent: "#7c3aed",
              shadow: "0 16px 32px -20px rgba(124, 58, 237, 0.9)",
            },
            {
              key: "shadowing",
              label: "Shadow Mode",
              icon: "🎭",
              desc: "Speak with the narrator",
              gradient: "linear-gradient(135deg, #f59e0b 0%, #f97316 100%)",
              tint: "#fffbeb",
              border: "#fde68a",
              iconTint: "#fef3c7",
              accent: "#d97706",
              shadow: "0 16px 32px -20px rgba(245, 158, 11, 0.9)",
            },
            {
              key: "manual",
              label: "Manual Mode",
              icon: "🎮",
              desc: "Play and record manually",
              gradient: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
              tint: "#ecfdf5",
              border: "#a7f3d0",
              iconTint: "#d1fae5",
              accent: "#059669",
              shadow: "0 16px 32px -20px rgba(16, 185, 129, 0.9)",
            },
          ].map((option) => {
            const active = practiceMode === option.key;
            const locked =
              (echoingPhase !== "idle" || recordingStatus === "recording") &&
              !active;

            return (
              <button
                key={option.key}
                type="button"
                onClick={() => {
                  cancelAllProcesses();
                  setPracticeMode(option.key);
                  setPreservedMode(option.key);

                  if (option.key === "writeEcho") {
                    setWriteEchoStage("write");
                    setViewMode("practice");
                    setVisualizationView("pitch");
                  }
                }}
                disabled={locked}
                aria-pressed={active}
                style={
                  active
                    ? {
                        backgroundImage: option.gradient,
                        borderColor: "transparent",
                        color: "#ffffff",
                        boxShadow: option.shadow,
                      }
                    : {
                        backgroundColor: option.tint,
                        borderColor: option.border,
                        color: "#0f172a",
                      }
                }
                className={[
                  "group relative flex w-full items-center gap-3 overflow-hidden rounded-2xl border px-3.5 py-3 text-left transition-all duration-200",
                  active
                    ? "-translate-y-px"
                    : "hover:-translate-y-px hover:brightness-[0.985] hover:shadow-md",
                  locked ? "cursor-not-allowed opacity-45" : "",
                ].join(" ")}
              >
                {active && (
                  <span className="pointer-events-none absolute -right-8 -top-10 h-24 w-24 rounded-full bg-white/15 blur-2xl" />
                )}

                <span
                  className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border text-lg shadow-sm"
                  style={
                    active
                      ? {
                          backgroundColor: "rgba(255,255,255,0.18)",
                          borderColor: "rgba(255,255,255,0.2)",
                        }
                      : {
                          backgroundColor: option.iconTint,
                          borderColor: option.border,
                        }
                  }
                >
                  {option.icon}
                </span>

                <span className="relative min-w-0 flex-1">
                  <span
                    className={[
                      "block truncate text-sm font-bold tracking-tight",
                      active ? "text-white" : "text-slate-900",
                    ].join(" ")}
                  >
                    {option.label}
                  </span>
                  <span
                    className={[
                      "mt-0.5 block truncate text-xs font-medium",
                      active ? "text-white/75" : "text-slate-500",
                    ].join(" ")}
                  >
                    {option.desc}
                  </span>
                </span>

                <span
                  className="relative flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-black"
                  style={
                    active
                      ? {
                          backgroundColor: "rgba(255,255,255,0.2)",
                          color: "#ffffff",
                        }
                      : {
                          backgroundColor: "rgba(255,255,255,0.78)",
                          color: option.accent,
                          border: `1px solid ${option.border}`,
                        }
                  }
                  aria-hidden="true"
                >
                  {active ? "✓" : "›"}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Visualization Controls - Desktop */}
      <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-5 border border-white/40 shadow-lg shadow-blue-500/5 flex-shrink-0">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-800 flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center text-white text-sm">
              📊
            </div>
            Visualization
          </h3>

          <button
            onClick={() =>
              setShowIntonationVisualization(!showIntonationVisualization)
            }
            className={`relative inline-flex h-7 w-12 items-center rounded-full transition-all duration-300 ${
              showIntonationVisualization
                ? "bg-gradient-to-r from-blue-500 to-indigo-500 shadow-md"
                : "bg-gray-300"
            }`}
          >
            <span
              className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-all duration-300 ${
                showIntonationVisualization ? "translate-x-6" : "translate-x-1"
              }`}
            />
          </button>
        </div>

        {showIntonationVisualization && (
          <div className="space-y-3">
            {[
              {
                key: "pitch",
                icon: "📈",
                label: "Pitch",
                color: "from-blue-500 to-cyan-500",
              },
              {
                key: "music",
                icon: "🎵",
                label: "Music Notes",
                color: "from-purple-500 to-pink-500",
              },
              {
                key: "combined",
                icon: "🌀",
                label: "Pitch + loudness",
                color: "from-indigo-500 to-purple-500",
              },
            ].map(({ key, icon, label, color }) => (
              <button
                key={key}
                onClick={() => setVisualizationView(key)}
                className={`w-full p-4 rounded-xl transition-all duration-300 flex items-center gap-3 border ${
                  visualizationView === key
                    ? `bg-gradient-to-r ${color} text-white shadow-lg border-transparent`
                    : "bg-white/60 text-gray-600 hover:bg-white/80 border-white/40 hover:border-white/60 hover:shadow-md"
                }`}
              >
                <span className="text-lg">{icon}</span>
                <span className="font-medium text-sm">{label}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="h-24 shrink-0" />
    </>
  )}
</div>
            {/* Main Content Panel */}
<div className="flex-1 min-w-0 min-h-0 flex flex-col overflow-y-auto bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-100/30">                  {/* Mobile Header */}
            <MobileHeader 
            language={subject?.language}
  mobileMenuOpen={mobileMenuOpen}
  setMobileMenuOpen={setMobileMenuOpen}
  setShowKeyboardHelp={setShowKeyboardHelp}
  showIntonationVisualization={showIntonationVisualization}
  setShowIntonationVisualization={setShowIntonationVisualization}
  visualizationView={practiceMode === "writeEcho" ? "pitch" : visualizationView}
  setVisualizationView={(nextView) => {
    if (practiceMode !== "writeEcho") setVisualizationView(nextView);
  }}
  practiceMode={practiceMode}
  setPracticeMode={(nextMode) => {
    setPracticeMode(nextMode);
    if (nextMode === "writeEcho") {
      setWriteEchoStage("write");
      setViewMode("practice");
      setVisualizationView("pitch");
    }
  }}
  cancelAllProcesses={cancelAllProcesses}
  setPreservedMode={setPreservedMode}
 isPublic={isPublic}
  todaySeconds={todaySeconds}
  goalSeconds={goalSeconds}
  setShowProgressModal={setShowProgressModal}
/>               
                {/* Keyboard Help Floating Button - Desktop */}
                {!isMobile && (
  <div className="fixed bottom-6 right-6 z-30">
                    <button
                        onClick={() => setShowKeyboardHelp(true)}
                        className="keyboard-help-button group p-3 bg-white/80 backdrop-blur-sm rounded-xl border border-white/40 shadow-lg shadow-blue-500/5 hover:bg-white transition-all duration-300"
                        title="Keyboard Shortcuts (Press ?)"
                    >
                        <MdKeyboard className="text-gray-600 group-hover:text-blue-600 text-xl transition-colors" />
                    </button>
                </div>) }

                {/* Content Area */}
       {narratorFragmentLoading && (
         <div className="mx-4 mt-4 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">
           Preparing the audio fragment…
         </div>
       )}
       {narratorFragmentError && (
         <div
           className="mx-4 mt-4 rounded-xl border border-amber-200/90 bg-amber-50/90 px-4 py-3 text-sm text-slate-700 shadow-sm"
           role="alert"
         >
           <div className="flex items-start gap-3">
             <span
               className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-amber-100 text-xs font-bold text-amber-700 ring-1 ring-amber-200"
               aria-hidden="true"
             >
               !
             </span>

             <div className="min-w-0">
               <div className="font-semibold text-slate-800">
                 Audio unavailable
               </div>
               <div className="mt-0.5 text-slate-600">
                 The narrator audio fragment could not be prepared.
               </div>

               <details className="mt-1.5">
                 <summary className="cursor-pointer select-none text-xs font-medium text-slate-500 hover:text-slate-700">
                   Technical details
                 </summary>
                 <div className="mt-1 break-all rounded-lg bg-white/75 px-2.5 py-2 font-mono text-[11px] leading-relaxed text-slate-600 ring-1 ring-slate-200/80">
                   {narratorFragmentError}
                 </div>
               </details>
             </div>
           </div>
         </div>
       )}
       {viewMode === "presentation" ? (
 <PresentationModeView
  audioref={audioref}
  fullaudio={fullaudio}
  timeUpdate={timeUpdate}
  segment={segment}
  segments={segments}
  handlePreviousSegment={handlePreviousSegment}
  handleNextSegment={handleNextSegment}
  isgranted={isgranted}
  getMicrophonePermission={getMicrophonePermission}
  practiceMode={practiceMode}
  recordingStatus={recordingStatus}
  isPlayingSegment={isPlayingSegment}
  recordingProgress={recordingProgress}
  handlePlayPause={handlePlayPause}
  handleMainAction={handleMainAction}
  audioFragmentBlocked={isNarratorFragmentBlocked}
  audioFragmentStatusText={narratorFragmentStatusText}
  getButtonConfig={getButtonConfig}
  rate={rate}
  setRate={setRate}
  loading={loading}
  assessment={assessment}
  subject={subject}
  file={file}
  handleLaneWordClick={handleLaneWordClick}
  broadcastTime={broadcastTime}
  visualizationView={visualizationView}
  auth={auth}
  resolvedAssessmentAudioUrl={resolvedAssessmentAudioUrl}
  resolvedNarratorAssessmentAudioUrl={resolvedNarratorAssessmentAudioUrl}
  canUsePronunciationApi={canUsePronunciationApi}
   echoingPhase={echoingPhase}
  isCueActive={isCueActive}
/>
) : (
  <PracticeModeView
  audioref={audioref}
  fullaudio={fullaudio}
  chapterTitle={sectionTitle}
  timeUpdate={timeUpdate}

  segments={segments}
  modelSize={modelSize}

  segment={segment}
  renderHeaderIdentity={renderHeaderIdentity}
  renderGuidePill={renderGuidePill}
  renderSentenceSharePill={renderSentenceSharePill}
  renderCommentsPill={renderCommentsPill}
  isMetadataNoHelp={isMetadataNoHelp}
  setShowExerciseHelp={setShowExerciseHelp}
  handlePreviousSegment={handlePreviousSegment}
  handleNextSegment={handleNextSegment}
  handleSelectSegment={handleSelectSegment}
  showCenterCue={showCenterCue}
  practiceMode={practiceMode}
  echoingPhase={echoingPhase}
  isCueActive={isCueActive}
  recordingStatus={recordingStatus}
  cuePulseCount={cuePulseCount}
  echoingPulseCount={echoingPulseCount}
  currentWord={currentWord}
  handleWordPlay={handleWordPlay}
  handlePlayFromWord={handlePlayFromWord}
  handleRangePlay={handleRangePlay}
  openWordMenu={openWordMenu}

  isgranted={isgranted}
  getMicrophonePermission={getMicrophonePermission}
  isPlayingSegment={isPlayingSegment}
  recordingProgress={recordingProgress}
  handlePlayPause={handlePlayPause}
  handleMainAction={handleMainAction}
  audioFragmentBlocked={isNarratorFragmentBlocked}
  audioFragmentStatusText={narratorFragmentStatusText}
  getButtonConfig={getButtonConfig}
  rate={rate}
  setRate={setRate}

  loading={loading}
  assessment={assessment}
  userRecordingUrl={audio}


  showIntonationVisualization={showIntonationVisualization}
  showIntonationHelp={showIntonationHelp}
  setShowIntonationHelp={setShowIntonationHelp}
  subject={subject}
  file={file}
  handleLaneWordClick={handleLaneWordClick}
  broadcastTime={broadcastTime}
  visualizationView={practiceMode === "writeEcho" ? "pitch" : visualizationView}
  auth={auth}
  resolvedAssessmentAudioUrl={resolvedAssessmentAudioUrl}
  resolvedNarratorAssessmentAudioUrl={resolvedNarratorAssessmentAudioUrl}

  canUsePronunciationApi={canUsePronunciationApi}
  onWriteEchoStageChange={handleWriteEchoStageChange}
  
  
/>
)}

                 <div className="h-24 xl:h-32 shrink-0" />
            </div>

            {/* Mobile Translation Modal */}
            {showTranslationModal && translation && (
                <div className="fixed inset-0 z-50 xl:hidden">
                    {/* Backdrop */}
                    <div 
                        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
                        onClick={() => setShowTranslationModal(false)}
                    />
                    
                    {/* Modal Content */}
                    <div className="absolute bottom-0 left-0 right-0 bg-white/95 backdrop-blur-sm rounded-t-3xl border-t border-white/30 shadow-2xl max-h-[85vh] overflow-hidden animate-in slide-in-from-bottom-1 duration-300">
                        <div className="p-6 max-h-[85vh] overflow-y-auto">
                            {/* Header */}
                            <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200">
                                <h2 className="font-bold text-gray-800 text-xl flex items-center gap-3">
                                    <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl flex items-center justify-center text-white">
                                        🌐
                                    </div>
                                    Translation
                                </h2>
                                <button
                                    onClick={() => setShowTranslationModal(false)}
                                    className="p-2 hover:bg-gray-100 rounded-xl transition-all duration-200"
                                >
                                    <span className="text-gray-500 text-2xl">×</span>
                                </button>
                            </div>

                            {/* Translation Content */}
                            <div className="space-y-4">
                                {translation.translation[0]?.hasOwnProperty("normalizedSource") ? (
                                    <div className='space-y-4'>
                                        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-200/50">
                                            <p className='font-semibold text-gray-800 text-lg'>
                                                {translation.translation[0].normalizedSource} 
                                            </p>
                                            <p className='text-xs text-gray-500 mt-1'>Normalized form</p>
                                        </div>
                                        <div>
                                            <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                                                <span className="text-amber-500">📖</span>
                                                Translations
                                            </h4>
                                            <ul className='space-y-3'>
                                                {translation.translation[0].translations.map((item, i) => (
                                                    <li key={i} className='flex items-start gap-3 p-3 rounded-xl bg-white/50 border border-gray-200'>
                                                        <span className='font-bold text-blue-500 bg-blue-100 rounded-lg px-2 py-1 text-xs flex-shrink-0'>{i+1}</span>
                                                        <span className="text-gray-700 flex-1">{item.displayTarget}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>
                                ) : (translation.translation === "missing translation") ? (
                                    <div className="text-center py-8 bg-white/50 rounded-xl border border-gray-200">
                                        <div className="text-4xl mb-3 opacity-50">🔍</div>
                                        <p className="text-gray-600 font-medium">Translation not available</p>
                                        <p className="text-gray-500 text-sm mt-1">Try a different word</p>
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-4 border border-green-200/50">
                                            <p className='font-semibold text-gray-800 text-lg'>
                                                {translation.word}
                                            </p>
                                            <p className='text-xs text-gray-500 mt-1'>Word to translate</p>
                                        </div>
                                        <div>
                                            <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                                                <span className="text-green-500">📚</span>
                                                Translations
                                            </h4>
                                            <ul className='space-y-3'>
                                                {translation.translation[0].translations.map((item, i) => (
                                                    <li key={i} className='flex items-start gap-3 p-3 rounded-xl bg-white/50 border border-gray-200'>
                                                        <span className='font-bold text-green-500 bg-green-100 rounded-lg px-2 py-1 text-xs flex-shrink-0'>{i+1}</span>
                                                        <span className="text-gray-700 flex-1">{item.text}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Close Button */}
                            <div className="mt-8 pt-4 border-t border-gray-200">
                                <button
                                    onClick={() => setShowTranslationModal(false)}
                                    className="w-full py-3 px-4 bg-gradient-to-r from-gray-600 to-gray-700 text-white rounded-xl font-medium transition-all duration-300 hover:shadow-lg"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                     
                </div>
              

            )}


            <WordActionMenu
  wordMenu={wordMenu}
  closeWordMenu={closeWordMenu}
  handleWordPlay={handleWordPlay}
  handleWordClick={handleWordClick}
  segment={segment}
/>

           {!isPublic && (
 <ProgressModal
  open={showProgressModal}
  onClose={() => setShowProgressModal(false)}
  doneSeconds={todaySeconds}
  goalSeconds={goalSeconds}
  minGoalSeconds={MIN_GOAL_SECONDS}   
  onGoalMinus={() => updateDailyGoal(goalSeconds - STEP_SECONDS)} // optional: simpler
  onGoalPlus={() => updateDailyGoal(goalSeconds + STEP_SECONDS)}
/>

)}
{helpMode === "context" ? (
  <ContextHelpModal
    open={showExerciseHelp}
    onClose={() => setShowExerciseHelp(false)}
    course={course}
    contextItem={currentContextItem}
    initialTab="overview"
  />
) : (
  <ExerciseHelpModal
    open={showExerciseHelp}
    onClose={() => setShowExerciseHelp(false)}
    exercise={hasExerciseHelp ? currentExercise : null}
    course={course}
    mode={hasExerciseHelp ? "exercise" : "about"}
  />
)}
{showIntonationHelp &&
  typeof document !== "undefined" &&
  createPortal(
    <IntonationHelpModal
      open={showIntonationHelp}
      onClose={() => setShowIntonationHelp(false)}
    />,
    document.body
  )}
<ChapterCommentsPanel
  open={showComments}
  onClose={() => setShowComments(false)}
  subject={subject}
  file={file}
  segment={segment}
  auth={auth}
  onCountsChange={setCommentCounts}
  chapterTitle={sectionTitle}
/>


        </div>
    );
    
};

export default SnapsWise;